/**
 * Strain-info overlay — Infographic v2 design.
 *
 * Opens in-widget (not a new tab) with description / effects / flavors /
 * terpenes / may-help / watch-for / lineage tree / FAQ / disclaimers.
 * Controlled by two conditions:
 *   1. Store has strain_popup_enabled=true (set on widget instance at boot)
 *   2. The specific product has master_products data (checked at click time)
 *
 * Design ported from user-provided "Infographic v2" (Apple-style refinement):
 * color-coded type pill, type-consensus split meter, THC + top-effect stats
 * row with inline per-source comparison, radar chart for effects, terpene
 * color bar + rich cards, flavor cloud (size scales with agreement), split
 * "May help" / "Watch for" rich stacks, family-tree SVG, FAQ, disclaimers.
 */

const OVERLAY_CSS = `
.vb-strain-overlay {
  position: absolute; top: 0; left: 0; right: 0; bottom: 0;
  background: #F5F5F7; z-index: 9999;
  /* NOTE: scroll moved off overlay onto .vb-pop--info2 (popup) so the
   * .info2-stickybar pins to popup's top — position:sticky pins to the
   * nearest scrollable ancestor and the bar lives inside popup. With
   * the scroll container on overlay, sticky pinned to popup which was
   * NOT a scroll container so the bar visually moved with the page.
   * Overlay now just lays out the popup at top-center; popup itself
   * scrolls within overlay's bounded box. */
  overflow: hidden;
  overscroll-behavior: contain;
  font-family: "SF Pro Text", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  color: #1D1D1F;
  -webkit-font-smoothing: antialiased;
  padding: 0; box-sizing: border-box; width: 100%;
  text-align: center;
}
.vb-strain-overlay > .vb-pop--info2 {
  display: inline-block; text-align: left;
  /* Popup is now the scroll container. Constrain to the overlay's
   * bounded box (overlay is position:absolute; inset:0 inside the
   * widget root) so anything past one viewport-height scrolls
   * INSIDE the popup, not the page. */
  max-height: 100%;
  vertical-align: top;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  /* Firefox thin themed scrollbar — thumb color set inline at render
   * time (--vb-strain-thumb), neutral fallback when no primary color
   * was plumbed. Mirrors the chat-body-premium scrollbar in
   * src/ui/styles.js + src/core/widget.js dynamicCSS. */
  scrollbar-width: thin;
  scrollbar-color: var(--vb-strain-thumb, rgba(0,0,0,0.18)) transparent;
}
/* WebKit thin themed scrollbar — matches #chat-body-premium width (6px). */
.vb-strain-overlay > .vb-pop--info2::-webkit-scrollbar {
  width: 6px;
}
.vb-strain-overlay > .vb-pop--info2::-webkit-scrollbar-track {
  background: transparent;
}
.vb-strain-overlay > .vb-pop--info2::-webkit-scrollbar-thumb {
  background: var(--vb-strain-thumb, rgba(0,0,0,0.18));
  border-radius: 3px;
}
.vb-strain-overlay > .vb-pop--info2::-webkit-scrollbar-thumb:hover {
  background: var(--vb-strain-thumb-hover, rgba(0,0,0,0.32));
}

/* "Curated strain info" trigger — Apple-style pill CTA.
 * Was a small green text-link; user wanted a real, obvious button.
 * Click opens the full-page strain modal directly (no inline preview).
 * Stays inside the existing green palette, no decorative icon — just a
 * forward chevron to read as "go". */
.vb-strain-learn-wrap {
  display: flex; justify-content: center;
  margin: 12px 0 4px;
}
.vb-strain-learn-link {
  display: inline-flex; align-items: center; gap: 8px;
  /* Themed via business primary color (set on :root by widget.js as
   * --chatbot-primary-color). Falls back to VirtualBudz green when the
   * variable isn't plumbed (older bundles, preview pages, tests). */
  background: var(--chatbot-primary-color, #2E8B30); color: #fff;
  border: none;
  padding: 10px 18px;
  font: 600 13px/1.3 "SF Pro Text", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  letter-spacing: 0.01em;
  border-radius: 999px;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0,0,0,0.06);
  transition: filter 0.15s, transform 0.1s, box-shadow 0.15s;
}
.vb-strain-learn-link:hover {
  /* Darker shade for hover via filter so we work with ANY primary color
   * (can't compute a literal shade from a CSS var without color-mix and
   * we want this to work on older browsers too). */
  filter: brightness(0.92);
  box-shadow: 0 4px 12px rgba(0,0,0,0.18);
}
.vb-strain-learn-link:active { transform: scale(0.98); }
.vb-strain-learn-text { font-weight: 600; }
.vb-strain-learn-chev {
  opacity: 0.95;
  flex-shrink: 0;
}

/* Inline preview — expands INSIDE the product card (appended at card end),
 * not as a sibling below. User said: "not making another card below it,
 * just expanding that card". */
.vb-strain-inline {
  /* Lives INSIDE the product card so no separate card shell — just a
   * soft divider + inner padding to set it apart from the product info
   * above. Full card width (card is block-level). */
  background: #fafbfc;
  border: 1px solid rgba(0,0,0,0.06);
  border-radius: 12px;
  padding: 14px 16px 12px;
  margin: 4px 0 0;
  font-family: "SF Pro Text", "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  color: #1D1D1F;
  width: 100%;
  box-sizing: border-box;
}
.vb-strain-inline-source-note {
  font-size: 11px; font-weight: 600;
  color: #2E8B30; letter-spacing: 0.02em;
  display: flex; align-items: center; gap: 6px;
  padding: 6px 10px; margin: 0 0 10px;
  background: rgba(92,179,53,0.08);
  border: 1px solid rgba(92,179,53,0.2);
  border-radius: 8px;
  line-height: 1.35;
}
.vb-strain-inline-source-note svg { flex-shrink: 0; opacity: 0.85; }
.vb-strain-inline-hdr {
  display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap;
  margin-bottom: 8px;
}
.vb-strain-inline-name {
  font-family: "SF Pro Display", "Inter", sans-serif;
  font-size: 17px; font-weight: 600; letter-spacing: -0.01em; color: #1D1D1F;
}
.vb-strain-inline-type {
  display: inline-flex; align-items: center;
  padding: 2px 9px; border-radius: 999px;
  font-size: 10.5px; font-weight: 600;
}
.vb-strain-inline-type--hybrid { background: rgba(92,179,53,0.12); color: #2E8B30; border: 1px solid rgba(92,179,53,0.35); }
.vb-strain-inline-type--sativa { background: rgba(249,115,22,0.12); color: #c2410c; border: 1px solid rgba(249,115,22,0.35); }
.vb-strain-inline-type--indica { background: rgba(139,92,246,0.12); color: #6d28d9; border: 1px solid rgba(139,92,246,0.35); }

.vb-strain-inline-desc-wrap { position: relative; }
.vb-strain-inline-desc {
  font-size: 13.5px; color: #1D1D1F; line-height: 1.55;
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical;
  overflow: hidden; margin: 0;
}
.vb-strain-inline-desc-wrap.open .vb-strain-inline-desc {
  -webkit-line-clamp: unset; overflow: visible;
}
.vb-strain-inline-chev {
  background: transparent; border: none; padding: 4px; cursor: pointer;
  color: #86868B; display: inline-flex; align-items: center; gap: 4px;
  font: 500 11.5px/1 inherit;
  border-radius: 6px;
  margin-top: 4px;
}
.vb-strain-inline-chev:hover { background: rgba(0,0,0,0.04); color: #1D1D1F; }
.vb-strain-inline-chev svg { transition: transform 0.15s; }
.vb-strain-inline-desc-wrap.open .vb-strain-inline-chev svg { transform: rotate(180deg); }

.vb-strain-inline-chips {
  display: flex; flex-wrap: wrap; gap: 5px 7px;
  margin-top: 10px;
}
.vb-strain-inline-chip {
  font-size: 11.5px; padding: 3px 10px; border-radius: 999px;
  background: rgba(16,185,129,0.08); color: #047857; border: 1px solid rgba(16,185,129,0.28);
  font-weight: 500;
}
.vb-strain-inline-chip--flavor {
  background: rgba(249,115,22,0.08); color: #9a3412; border-color: rgba(249,115,22,0.25);
}
.vb-strain-inline-actions {
  display: flex; align-items: center; gap: 10px; margin-top: 14px;
  padding-top: 10px; border-top: 1px dashed rgba(0,0,0,0.08);
  flex-wrap: wrap;
}
.vb-strain-inline-full {
  background: #0A1B3D; color: #fff; border: none;
  padding: 8px 16px; border-radius: 999px;
  font: 500 12.5px/1.2 inherit; cursor: pointer;
  display: inline-flex; align-items: center; gap: 6px;
  transition: background 0.15s;
  flex: 1 1 auto;
  justify-content: center;
  min-width: 0;
}
.vb-strain-inline-full:hover { background: #15274D; }
.vb-strain-inline-full svg { opacity: 0.85; flex-shrink: 0; }
.vb-strain-inline-close {
  background: transparent; border: 1px solid rgba(0,0,0,0.12);
  border-radius: 999px; padding: 8px 14px; cursor: pointer;
  color: #6E6E73; font: 500 12px/1.2 inherit;
}
.vb-strain-inline-close:hover { color: #1D1D1F; border-color: rgba(0,0,0,0.2); }
.vb-strain-inline-empty { font-size: 12px; color: #86868B; font-style: italic; padding: 4px 0; }
/* Animated loading dots — when the empty-state container ends with "…",
   we strip the static ellipsis in the markup and let CSS animate three
   dots after the text via ::after. So "Loading strain info" reads with
   one, two, then three pulsing dots that cycle every 1.4s. Doesn't
   affect non-loading messages ("No curated data for this strain.")
   because they get the .vb-strain-inline-empty--static modifier. */
.vb-strain-inline-empty.vb-strain-inline-empty--loading::after {
  content: "";
  display: inline-block;
  width: 1em;
  text-align: left;
  animation: vb-strain-loading-dots 1.4s infinite steps(1, end);
}
@keyframes vb-strain-loading-dots {
  0%   { content: ""; }
  25%  { content: "."; }
  50%  { content: ".."; }
  75%  { content: "..."; }
  100% { content: ""; }
}

/* Loading skeleton inside the strain overlay popup. Rendered the moment
 * the user clicks the trigger so the overlay appears instantly while the
 * /strain-info fetch is in flight. Replaced wholesale with the real
 * infographic markup once the fetch resolves. Sized to fill the popup
 * body so the spinner sits comfortably in the center of the viewport.
 *
 * Error fallback (.info2-skeleton--error) reuses the same shell so
 * dimensions don't jump when we swap the spinner for a retry button. */
.info2-skeleton {
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  min-height: 280px; padding: 40px 20px;
  text-align: center;
  gap: 14px;
  color: #6E6E73;
  font: 500 14px/1.4 "SF Pro Text", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}
.info2-skeleton-spinner {
  width: 28px; height: 28px; border-radius: 50%;
  border: 2.5px solid rgba(0,0,0,0.08);
  border-top-color: var(--vb-strain-thumb, #2E8B30);
  animation: vb-strain-skel-spin 0.85s linear infinite;
}
@keyframes vb-strain-skel-spin { to { transform: rotate(360deg); } }
.info2-skeleton-text { font-size: 14px; font-weight: 500; color: #6E6E73; }
.info2-skeleton--error { color: #1D1D1F; }
.info2-skeleton-retry {
  margin-top: 6px;
  background: var(--chatbot-primary-color, #2E8B30); color: #fff;
  border: none; padding: 10px 18px; border-radius: 999px;
  font: 600 13px/1.3 inherit; cursor: pointer;
  transition: filter 0.15s, transform 0.1s;
}
.info2-skeleton-retry:hover { filter: brightness(0.92); }
.info2-skeleton-retry:active { transform: scale(0.98); }
.info2-skeleton--empty { color: #1D1D1F; }
.info2-skeleton-close {
  margin-top: 6px;
  background: #f5f5f7; color: #1d1d1f;
  border: 1px solid #d2d2d7; padding: 10px 18px; border-radius: 999px;
  font: 600 13px/1.3 inherit; cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s, transform 0.1s;
}
.info2-skeleton-close:hover { background: #ebebed; border-color: #86868b; }
.info2-skeleton-close:active { transform: scale(0.98); }

.vb-pop--info2 {
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.04), 0 20px 50px rgba(0,0,0,0.12);
  /* horizontal overflow stays hidden so rounded corners clip cleanly,
   * but vertical overflow is now scroll (set in the overlay > popup rule
   * above) so sticky bar pins to popup top. */
  overflow-x: hidden;
  width: 100%;
  max-width: 100%;
  margin: 0;
  box-sizing: border-box;
  position: relative;
}

/* Legacy header (kept for any code path that still appends .info2-hdr).
 * The new sticky-bar replaces it for openStrainOverlay; rules below are
 * harmless if not rendered. */
.info2-hdr { display: flex; justify-content: space-between; align-items: center; padding: 14px 18px 10px; }
.info2-hdr-title { font-size: 12px; font-weight: 600; color: #86868B; text-transform: uppercase; letter-spacing: 0.08em; }
.info2-close {
  background: transparent; border: none; width: 28px; height: 28px;
  border-radius: 999px; display: flex; align-items: center; justify-content: center;
  cursor: pointer; color: rgba(0,0,0,0.45);
  transition: background .2s cubic-bezier(0.28, 0.11, 0.32, 1);
  padding: 0;
}
.info2-close:hover { background: rgba(0,0,0,0.04); }
.info2-close svg { display: block; }

/* Sticky nav bar — pinned to top of the strain overlay scroll container
 * so the back button is always reachable while scrolling through
 * effects, terpenes, lineage, FAQ, etc. Translucent w/ backdrop blur to
 * sit cleanly over the white popup beneath. */
.info2-stickybar {
  /* top:-1px instead of 0 to hide the sub-pixel hairline gap some
   * browsers expose between the rounded popup top and the sticky
   * bar's flat top edge when the bar pins at scroll-top=0. */
  position: sticky; top: -1px; z-index: 100;
  /* Solid fallback first so browsers w/o backdrop-filter (older Firefox)
   * still get a fully-readable opaque bar instead of see-through chrome. */
  background: #ffffff;
  background: rgba(255,255,255,0.85);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  padding: 10px 16px;
  display: flex; align-items: center; gap: 10px;
  border-bottom: 1px solid transparent;
  /* Match the popup's rounded top corners so the bar doesn't square off
   * the upper edge while pinned at scroll-top. As the user scrolls, the
   * border-radius keeps the bar visually consistent with the popup. */
  border-top-left-radius: 18px;
  border-top-right-radius: 18px;
  /* Shadow + hairline are JS-toggled (.info2-stickybar--scrolled) so the
   * bar floats flush at scroll-top and gains a clear pinned-nav feel
   * once content slides beneath it. */
  transition: box-shadow .18s cubic-bezier(0.28, 0.11, 0.32, 1),
              border-color .18s cubic-bezier(0.28, 0.11, 0.32, 1);
}
.info2-stickybar--scrolled {
  box-shadow: 0 1px 0 rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.04);
  border-bottom-color: rgba(0,0,0,0.08);
}
.info2-stickybar--scrolled .info2-stickybar-title { font-weight: 700; }
.info2-stickybar-back {
  display: inline-flex; align-items: center; gap: 6px;
  background: none; border: none; padding: 6px 10px;
  font: 500 13px/1.3 "SF Pro Text", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  color: #1D1D1F; cursor: pointer; border-radius: 8px;
  transition: background 0.15s;
}
.info2-stickybar-back:hover { background: rgba(0,0,0,0.05); }
.info2-stickybar-back svg { display: block; flex-shrink: 0; }
.info2-stickybar-title {
  flex: 1; text-align: center;
  font: 600 14px/1.3 "SF Pro Text", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  color: #1D1D1F;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  /* Reserve symmetry with the back button width so the title stays
   * centered. The empty spacer on the right matches this width. */
  padding: 0 4px;
}
.info2-stickybar-spacer {
  /* Visual balance — same approximate width as the back button so the
   * centered title doesn't drift left. */
  width: 56px; flex-shrink: 0;
}
/* Used to be 4px 22px — user wanted "full width" so we shrank the
   side gutter from 22px → 10px (about 24px wider on each side at the
   440px popup max-width). Orbital still bleeds outside this with its
   own negative margin so it touches the popup edge. */
.info2-body { padding: 4px 14px 22px; }

.info2-title { text-align: center; padding: 4px 0 14px; }
/* Small muted caption above the strain hero h1. Signals to the user that
   what follows is STRAIN-level info (community + catalog data), not info
   about the specific dispensary product they clicked. 12px, uppercased,
   wide letter-spacing — Apple-strict, no decoration. */
.info2-title-caption {
  font-size: 11px; font-weight: 600; color: #86868B;
  text-transform: uppercase; letter-spacing: 0.08em;
  margin-bottom: 2px;
}
.info2-name {
  font-family: "SF Pro Display", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  font-size: 30px; font-weight: 600; letter-spacing: -0.025em;
  margin: 6px 0 2px; color: #1D1D1F; line-height: 1.1;
}
.info2-aliases { font-size: 11.5px; color: #86868B; font-style: italic; }

.info2-type-pill {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 4px 11px; border-radius: 999px;
  font-size: 11.5px; font-weight: 600; letter-spacing: 0.02em;
  margin-top: 8px;
}
.info2-type-pill--hybrid { background: rgba(92,179,53,0.12); color: #2E8B30; border: 1px solid rgba(92,179,53,0.35); }
.info2-type-pill--sativa { background: rgba(249,115,22,0.12); color: #c2410c; border: 1px solid rgba(249,115,22,0.35); }
.info2-type-pill--indica { background: rgba(139,92,246,0.12); color: #6d28d9; border: 1px solid rgba(139,92,246,0.35); }

.info2-type-sw { width: 8px; height: 8px; border-radius: 2px; display: inline-block; flex-shrink: 0; }
.info2-type-sw--hybrid { background: #5CB335; }
.info2-type-sw--sativa { background: #F97316; }
.info2-type-sw--indica { background: #8B5CF6; }

.info2-type-consensus {
  padding: 12px 14px; margin: 4px 0 14px;
  background: #F5F5F7; border-radius: 12px;
}
.info2-type-meter {
  display: flex; height: 10px; border-radius: 5px; overflow: hidden; margin-bottom: 10px;
  background: rgba(0,0,0,0.06);
}
.info2-type-fill { display: block; }
.info2-type-fill[data-type="hybrid"] { background: linear-gradient(90deg, #5CB335, #8FD86B); }
.info2-type-fill[data-type="sativa"] { background: linear-gradient(90deg, #F97316, #FDBA74); }
.info2-type-fill[data-type="indica"] { background: linear-gradient(90deg, #8B5CF6, #C4B5FD); }
.info2-type-legend { display: flex; align-items: center; gap: 10px; font-size: 12px; }
.info2-type-legend-item { display: inline-flex; align-items: center; gap: 6px; color: #1D1D1F; }
.info2-type-legend-item.alt { color: #6E6E73; }
.info2-type-legend-num {
  font-family: "SF Pro Display", "Inter", sans-serif;
  font-weight: 700; font-size: 15px; letter-spacing: -0.02em;
}
.info2-type-legend-lbl { font-weight: 500; }
.info2-type-legend-sep { color: #86868B; font-size: 11px; font-style: italic; }
.info2-type-legend-total { margin-left: auto; font-size: 11px; color: #86868B; }

.info2-stats-row {
  display: grid; grid-template-columns: 1.6fr auto 1fr; gap: 0;
  padding: 14px 16px; margin-bottom: 14px;
  background: linear-gradient(135deg, rgba(92,179,53,0.08), rgba(92,179,53,0.02));
  border-radius: 12px;
  align-items: stretch;
}
.info2-stats-divider { width: 1px; background: rgba(0,0,0,0.1); margin: 0 14px; }

.info2-pop-block { display: flex; flex-direction: column; gap: 6px; align-items: flex-end; }
.info2-pop-label { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.12em; color: #86868B; }
.info2-pop-dots { display: inline-flex; gap: 3px; }
.info2-pop-dots span {
  width: 9px; height: 9px; border-radius: 50%; background: rgba(0,0,0,0.12);
  transition: background 0.2s;
}
.info2-pop-dots span.on { background: #5CB335; }
.info2-pop-rating {
  font-family: "SF Pro Display", "Inter", sans-serif;
  font-size: 18px; font-weight: 700; letter-spacing: -0.02em; color: #1D1D1F;
  font-variant-numeric: tabular-nums;
  display: inline-flex; align-items: baseline; gap: 4px;
}
.info2-pop-rating .info2-pop-star { color: #5CB335; font-size: 15px; }
.info2-pop-tier {
  font-family: "SF Pro Display", "Inter", sans-serif;
  font-size: 13px; font-weight: 600; letter-spacing: -0.01em; color: #1D1D1F;
  margin-top: 2px;
}
.info2-pop-meta { font-size: 10.5px; color: #86868B; font-variant-numeric: tabular-nums; }

.info2-top-effect { display: flex; flex-direction: column; gap: 4px; }
.info2-top-effect-label { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.1em; color: #86868B; }
.info2-top-effect-main { display: flex; align-items: baseline; }
.info2-top-effect-name {
  font-family: "SF Pro Display", "Inter", sans-serif;
  font-size: 18px; font-weight: 600; letter-spacing: -0.02em; color: #1D1D1F;
}
.info2-top-effect-stats { display: flex; flex-direction: column; gap: 2px; font-size: 11px; color: #6E6E73; }
.info2-top-effect-stats strong { color: #1D1D1F; font-weight: 600; font-variant-numeric: tabular-nums; }

.info2-viz {
  margin: 8px 0 18px; padding: 16px;
  background: linear-gradient(135deg, rgba(92,179,53,0.05), rgba(14,165,233,0.04));
  border-radius: 14px;
}
.info2-viz-label {
  font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.1em;
  color: #86868B; text-align: center; margin-bottom: 8px;
}
.info2-radar-wrap { display: flex; justify-content: center; padding: 0 4px; }
.info2-radar { max-width: 360px; height: auto; overflow: visible; }
.info2-radar-empty { font-size: 12px; color: #86868B; text-align: center; padding: 40px 0; }

.info2-terp-bar {
  display: flex; height: 12px; border-radius: 6px; overflow: hidden; margin: 14px 0 10px;
  box-shadow: inset 0 0 0 1px rgba(0,0,0,0.04);
}
.info2-terp-seg { position: relative; }
.info2-terp-seg[data-family="caryophyllene"] { background-color: #F97316; }
.info2-terp-seg[data-family="myrcene"]       { background-color: #F59E0B; }
.info2-terp-seg[data-family="limonene"]      { background-color: #EAB308; }
.info2-terp-seg[data-family="pinene"]        { background-color: #10B981; }
.info2-terp-seg[data-family="linalool"]      { background-color: #A855F7; }
.info2-terp-seg[data-family="humulene"]      { background-color: #EC4899; }
.info2-terp-seg[data-family="terpinolene"]   { background-color: #06B6D4; }
.info2-terp-seg[data-family="ocimene"]       { background-color: #84CC16; }
.info2-terp-seg[data-family="default"]       { background-color: #9ca3af; }
/* Store-only batch terpene segments — same family color as the universal
   palette but overlaid with a diagonal stripe texture so the customer can
   see at a glance which slice came from the store's lab vs from our
   merged universal sources. Solid segments = universal consensus, striped
   segments = "your batch reports this terpene". */
.info2-terp-seg.store-only-extra {
  background-image: repeating-linear-gradient(
    45deg,
    rgba(255, 255, 255, 0.45) 0,
    rgba(255, 255, 255, 0.45) 3px,
    transparent 3px,
    transparent 7px
  );
}

.info2-terp-cards {
  list-style: none; padding: 0; margin: 8px 0 0;
  display: flex; flex-direction: column; gap: 6px;
}
.info2-terp-card {
  background: #fff; border: 1px solid rgba(0,0,0,0.08); border-radius: 10px;
  padding: 10px 12px;
}
.info2-terp-card-hdr { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
.info2-terp-sw { width: 10px; height: 10px; border-radius: 3px; }
.info2-terp-sw[data-family="caryophyllene"] { background-color: #F97316; }
.info2-terp-sw[data-family="myrcene"]       { background-color: #F59E0B; }
.info2-terp-sw[data-family="limonene"]      { background-color: #EAB308; }
.info2-terp-sw[data-family="pinene"]        { background-color: #10B981; }
.info2-terp-sw[data-family="linalool"]      { background-color: #A855F7; }
.info2-terp-sw[data-family="humulene"]      { background-color: #EC4899; }
.info2-terp-sw[data-family="terpinolene"]   { background-color: #06B6D4; }
.info2-terp-sw[data-family="ocimene"]       { background-color: #84CC16; }
.info2-terp-sw[data-family="default"]       { background-color: #9ca3af; }
.info2-terp-name { font-weight: 600; font-size: 13px; color: #1D1D1F; }
.info2-terp-pct {
  margin-left: auto; font-size: 12px; font-variant-numeric: tabular-nums; color: #1D1D1F; font-weight: 500;
}
.info2-terp-pct em { font-style: italic; color: #86868B; font-weight: 400; }
.info2-terp-meta {
  display: flex; flex-direction: column; gap: 3px;
  font-size: 11.5px; color: #6E6E73; line-height: 1.45;
}
.info2-terp-meta em {
  font-style: normal; font-weight: 600; color: #86868B;
  text-transform: uppercase; font-size: 10px; letter-spacing: 0.08em; margin-right: 4px;
}
.info2-terp-sources {
  display: flex; align-items: center; gap: 8px; margin-top: 4px;
  font-size: 10.5px; color: #86868B;
}
.info2-terp-src-bars { display: inline-flex; gap: 2px; }
.info2-terp-src-bars span { width: 4px; height: 10px; border-radius: 1.5px; background: rgba(0,0,0,0.1); }
.info2-terp-src-bars span.on { background: #5CB335; }
.info2-terp-empty { font-size: 12px; color: #86868B; text-align: center; padding: 20px 0; font-style: italic; }

/* Store-batch indicator inline on each terpene card.
 * - Default (batch present): small green line, "Store batch: 0.42%".
 * - Missing variant: muted italic note appended at the bottom of the card
 *   to make it explicit that the store batch did not list this terpene
 *   even though universal sources report it. */
/* Removed in favor of inline .info2-terp-batch-chip (right of percent). */
.info2-terp-batch-chip {
  display: inline-block;
  padding: 2px 8px;
  margin-left: auto;
  font-size: 11px;
  font-weight: 600;
  color: #2E8B30;
  background: rgba(46, 139, 48, 0.10);
  border: 1px solid rgba(46, 139, 48, 0.30);
  border-radius: 999px;
  white-space: nowrap;
}
/* Highlight catalog-only terpenes more prominently — user wants the
   gap surfaced clearly: 'these are not in your batch but other sources
   say may be present'. */
.info2-terp-card-batch--missing {
  display: block;
  margin-top: 8px;
  padding: 8px 10px;
  font-size: 12px;
  color: #8a4a1c;
  background: rgba(201, 75, 30, 0.06);
  border-left: 3px solid #C94B1E;
  border-radius: 6px;
  font-style: normal;
  font-weight: 500;
}

.info2-desc {
  font-size: 14px; color: #1D1D1F; line-height: 1.5;
  margin: 10px 0 6px;
}
.info2-arb-chip {
  display: inline-block; margin: 6px 0 2px;
  font-size: 11px; font-weight: 500; color: #6e6e73;
  letter-spacing: 0.02em;
  text-transform: none;
}
.info2-arb-chip-low { color: #8a6d3b; }
.info2-desc-wrap { margin: 10px 0 6px; }
.info2-desc-collapsed {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: 14px; color: #1D1D1F; line-height: 1.5;
}
.info2-desc-full {
  font-size: 14px; color: #1D1D1F; line-height: 1.5;
  display: none;
}
.info2-desc-wrap.open .info2-desc-collapsed { display: none; }
.info2-desc-wrap.open .info2-desc-full { display: block; }
.info2-desc-toggle {
  display: inline-flex; align-items: center; gap: 4px;
  background: transparent; border: none; padding: 4px 0; margin: 2px 0 0;
  cursor: pointer;
  font: 500 12px/1 inherit;
  color: #2E8B30;
}
.info2-desc-toggle:hover { color: #0A1B3D; }
.info2-desc-toggle svg { transition: transform 0.15s; }
.info2-desc-wrap.open .info2-desc-toggle svg { transform: rotate(180deg); }
.info2-desc-variance {
  margin: 0 0 18px;
  border: 1px solid rgba(0,0,0,0.08); border-radius: 8px;
  padding: 6px 12px;
  background: #fafafa;
}
.info2-desc-variance > summary {
  cursor: pointer; font-size: 11.5px; color: #6E6E73; font-weight: 500;
  list-style: none; outline: none; padding: 4px 0;
}
.info2-desc-variance > summary::-webkit-details-marker { display: none; }
.info2-desc-variance[open] > summary { margin-bottom: 8px; border-bottom: 1px dashed rgba(0,0,0,0.08); padding-bottom: 6px; }
.info2-desc-src { margin-bottom: 10px; }
.info2-desc-src:last-child { margin-bottom: 4px; }
.info2-desc-src-label {
  font-size: 10px; font-weight: 700; color: #86868B; text-transform: uppercase;
  letter-spacing: 0.1em; display: block; margin-bottom: 2px;
  font-family: ui-monospace, monospace;
}
.info2-desc-src-body { font-size: 12.5px; color: #1D1D1F; line-height: 1.5; }

.info2-section { margin-top: 18px; }
.info2-section h3 {
  font-family: "SF Pro Display", "Inter", sans-serif;
  font-size: 12px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.1em; color: #86868B;
  margin: 0 0 10px; padding: 0;
}

.info2-flavor-cloud {
  display: flex; flex-wrap: wrap; gap: 10px 14px; align-items: baseline;
  padding: 14px 16px;
  background: linear-gradient(135deg, rgba(249,115,22,0.06), rgba(234,179,8,0.06));
  border-radius: 12px;
}
.info2-flavor {
  font-family: "SF Pro Display", "Inter", sans-serif;
  color: #9a3412; font-weight: 500; line-height: 1;
}

.info2-split { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-top: 16px; }

.info2-rich-stack { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 10px; }
.info2-rich-stack li { display: flex; flex-direction: column; gap: 4px; }
.info2-rich-top { display: flex; justify-content: space-between; align-items: baseline; font-size: 12.5px; }
.info2-rich-name { font-weight: 500; color: #1D1D1F; }
.info2-rich-votes { font-variant-numeric: tabular-nums; font-weight: 600; color: #1D1D1F; font-size: 12px; }
.info2-rich-bar { height: 4px; background: rgba(0,0,0,0.06); border-radius: 2px; overflow: hidden; }
.info2-rich-bar span { display: block; height: 100%; border-radius: 2px; }
.info2-rich-stack[data-tone="sky"] .info2-rich-bar span { background: linear-gradient(90deg, #0EA5E9, #7DD3FC); }
.info2-rich-stack[data-tone="rose"] .info2-rich-bar span { background: linear-gradient(90deg, #F43F5E, #FDA4AF); }
.info2-rich-sources { display: flex; align-items: center; gap: 6px; font-size: 10.5px; color: #86868B; }
.info2-rich-src-bars { display: inline-flex; gap: 2px; }
.info2-rich-src-bars span { width: 4px; height: 9px; border-radius: 1.5px; background: rgba(0,0,0,0.1); }
.info2-rich-stack[data-tone="sky"] .info2-rich-src-bars span.on { background: #0EA5E9; }
.info2-rich-stack[data-tone="rose"] .info2-rich-src-bars span.on { background: #F43F5E; }

.info2-tree-wrap { padding: 6px 0; background: #F5F5F7; border-radius: 12px; overflow: hidden; }
.info2-tree-svg { display: block; width: 100%; }

/* Filterable orbital lineage — full-circle node distribution with
   chip filters. Matches the reference design (Lineage E). HTML pill
   nodes positioned absolutely (not SVG circles) so labels stay legible
   regardless of ring radius. */
/* Body has 10px L/R padding now. Orbital uses negative margin to bleed
   to the true popup edge. Filters get a small inset so chips don't
   touch the edge. */
.info2-orbit-section {
  margin: 14px -14px 0;
}
.info2-orbit-filters {
  display: flex; flex-wrap: wrap; gap: 6px;
  padding: 0 14px 12px;
}
.info2-orbit-chip {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 11px; border-radius: 999px;
  background: #fff; border: 1px solid rgba(0,0,0,0.14);
  font-size: 11.5px; font-weight: 500; color: #6E6E73;
  cursor: pointer; user-select: none;
  transition: background 0.18s, border-color 0.18s, color 0.18s;
  font-family: inherit;
}
.info2-orbit-chip:hover { color: #1D1D1F; border-color: #86868B; }
.info2-orbit-chip[data-on="true"] {
  background: #1D1D1F; color: #fff; border-color: #1D1D1F;
}
.info2-orbit-chip[data-on="true"] .info2-orbit-chip-dot {
  box-shadow: 0 0 0 2px rgba(255,255,255,0.25);
}
.info2-orbit-chip-dot {
  width: 8px; height: 8px; border-radius: 999px;
  display: inline-block;
}
.info2-orbit-stage {
  position: relative;
  width: 100%; aspect-ratio: 1 / 1;
  background: radial-gradient(circle at center, #fff 0%, #FAFAFB 70%);
  /* Edge-to-edge — no border-radius since we're flush with the popup
     edges. The popup itself has radius 18px clipping the bottom. */
  overflow: hidden;
}
.info2-orbit-rings {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  pointer-events: none;
}
.info2-orbit-rings circle {
  fill: none; stroke-width: 1.2; stroke-dasharray: 2 4;
  opacity: 0.45; transition: opacity 0.25s ease;
}
.info2-orbit-rings circle[data-hidden="true"] { opacity: 0; }
.info2-orbit-nucleus {
  position: absolute; left: 50%; top: 50%;
  transform: translate(-50%, -50%);
  background: #0A1B3D; color: #fff;
  padding: 8px 14px; border-radius: 999px;
  font-size: 12.5px; font-weight: 600;
  box-shadow: 0 8px 24px rgba(10,27,61,0.3);
  pointer-events: none; z-index: 5;
  text-align: center;
  /* Tight pill — tightly hugs the strain name so inner-ring nodes at
     22% radius don't collide with it. Matches reference proportions. */
  max-width: 38%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
}
.info2-orbit-nucleus small {
  display: block;
  font-size: 10px; font-weight: 400;
  opacity: 0.7;
  text-transform: uppercase; letter-spacing: 0.06em;
  margin-top: 2px;
}
.info2-orbit-node {
  position: absolute;
  transform: translate(-50%, -50%);
  background: #fff; border: 1px solid rgba(0,0,0,0.08);
  border-radius: 999px;
  padding: 5px 10px; font-size: 10.5px; font-weight: 500;
  color: #1D1D1F; white-space: nowrap;
  box-shadow: 0 2px 6px rgba(0,0,0,0.06);
  transition: opacity 0.25s ease,
              transform 0.35s cubic-bezier(0.28, 0.11, 0.32, 1),
              box-shadow 0.18s ease;
  z-index: 2;
  cursor: pointer;
}
.info2-orbit-node:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.14);
  transform: translate(-50%, -50%) scale(1.06);
}
.info2-orbit-breadcrumb {
  padding: 0 22px 8px;
  font-size: 11px; color: #6E6E73;
  display: flex; flex-wrap: wrap; gap: 4px; align-items: center;
}
.info2-orbit-breadcrumb-link {
  color: #0A1B3D; cursor: pointer; text-decoration: none;
  border-bottom: 1px dotted rgba(10,27,61,0.3);
}
.info2-orbit-breadcrumb-link:hover { border-bottom-color: #0A1B3D; }
.info2-orbit-breadcrumb-current { color: #1D1D1F; font-weight: 600; }
.info2-orbit-breadcrumb-sep { color: #C7C7CC; }
.info2-orbit-loading {
  position: absolute; inset: 0;
  display: flex; align-items: center; justify-content: center;
  background: rgba(255,255,255,0.65);
  backdrop-filter: blur(2px);
  font-size: 11px; color: #6E6E73;
  pointer-events: none;
  opacity: 0; transition: opacity 0.15s ease;
  z-index: 6;
}
.info2-orbit-loading[data-state="visible"] { opacity: 1; }
/* Animated dots on the lineage loading overlay — strip the static
   ellipsis from the text and let CSS pulse three dots after it. */
.info2-orbit-loading::after {
  content: "";
  display: inline-block;
  width: 1em;
  text-align: left;
  animation: vb-strain-loading-dots 1.4s infinite steps(1, end);
}
.info2-orbit-node[data-gen="parents"] {
  border-color: rgba(92,179,53,0.4);
  background: rgba(92,179,53,0.08);
  color: #2E8B30; font-weight: 600;
}
.info2-orbit-node[data-gen="children"] {
  border-color: rgba(14,165,233,0.4);
  background: rgba(14,165,233,0.06);
  color: #0369A1;
}
.info2-orbit-node[data-gen="gp"] {
  font-size: 10px; opacity: 0.8;
}
.info2-orbit-node[data-gen="descendants"] {
  border-color: rgba(249,115,22,0.4);
  color: #C2410C;
  font-size: 10px;
}
.info2-orbit-node[data-hidden="true"] {
  opacity: 0; pointer-events: none;
  transform: translate(-50%, -50%) scale(0.6);
}
/* ───────── V3 lineage halo + badge (May 2026) ─────────
 * Tier mapping (1-indexed; tier=0 hides the node entirely):
 *   tier 0  →  source_count = 0  → hide
 *   tier 1  →  1-4 sources       → faint default shadow
 *   tier 2  →  5-19 sources      → mid green halo + border
 *   tier 3  →  20+ sources       → big green halo + bold border
 * Pill size stays UNIFORM across tiers — halo carries the visual
 * weight. (V3 won over V1/V2 because layout doesn't jump.) */
.info2-orbit-node[data-tier="0"] { display: none; }
.info2-orbit-node[data-tier="1"] {
  box-shadow: 0 2px 6px rgba(0,0,0,0.06);
}
.info2-orbit-node[data-tier="2"] {
  box-shadow: 0 0 0 5px rgba(46,139,48,0.10),
              0 3px 9px rgba(46,139,48,0.14);
  border-color: rgba(46,139,48,0.40);
}
.info2-orbit-node[data-tier="3"] {
  box-shadow: 0 0 0 8px rgba(46,139,48,0.10),
              0 0 0 14px rgba(46,139,48,0.05),
              0 4px 12px rgba(46,139,48,0.20);
  border-color: rgba(46,139,48,0.65);
}
/* Source-count badge — top-right of pill. Color saturates with tier. */
.info2-orbit-node-badge {
  position: absolute;
  top: -6px; right: -6px;
  min-width: 16px; height: 16px;
  padding: 0 3px;
  border-radius: 999px;
  background: #2E8B30; color: #fff;
  font-size: 9px; font-weight: 700;
  display: inline-flex; align-items: center; justify-content: center;
  box-shadow: 0 1px 3px rgba(0,0,0,0.18);
  letter-spacing: 0;
  pointer-events: none;
}
.info2-orbit-node[data-tier="1"] .info2-orbit-node-badge { background: #A1A1A6; }
.info2-orbit-node[data-tier="2"] .info2-orbit-node-badge { background: #5DA15F; }
.info2-orbit-node[data-tier="3"] .info2-orbit-node-badge { background: #2E8B30; }
/* Caption directly below the orbital stage explaining halo encoding. */
.info2-orbit-legend {
  text-align: center;
  padding: 6px 14px 0;
  font-size: 11px; color: #6E6E73;
  font-style: italic;
}
.info2-orbit-tip {
  position: absolute;
  background: #1D1D1F; color: #fff;
  padding: 6px 10px; border-radius: 8px;
  font-size: 11px; line-height: 1.3;
  white-space: nowrap; pointer-events: none;
  opacity: 0;
  transform: translate(-50%, calc(-100% - 12px));
  transition: opacity 0.15s ease;
  z-index: 10;
  box-shadow: 0 4px 12px rgba(0,0,0,0.18);
}
.info2-orbit-tip[data-state="visible"] { opacity: 1; }
.info2-orbit-tip strong { display: block; font-weight: 600; }
.info2-orbit-tip small { opacity: 0.7; font-size: 10px; }

/* Per-flavor source-attribution tooltip. Same dark popover language as
   the orbital tip (so the widget reads as a single design system), but
   wider, with a small breakdown table inside. Positioned in document
   coords (fixed) so it can escape the popup's clipping context. */
.info2-flavor-tip {
  position: fixed;
  background: #1D1D1F; color: #fff;
  padding: 8px 10px 9px; border-radius: 8px;
  font-size: 11px; line-height: 1.35;
  pointer-events: none;
  opacity: 0;
  transform: translate(-50%, calc(-100% - 8px));
  transition: opacity 0.12s ease;
  z-index: 2147483647;
  box-shadow: 0 6px 18px rgba(0,0,0,0.22);
  max-width: 240px; min-width: 160px;
  font-family: "SF Pro Text", "Inter", system-ui, sans-serif;
}
.info2-flavor-tip[data-state="visible"] { opacity: 1; }
.info2-flavor-tip-name {
  font-size: 12px; font-weight: 600;
  margin-bottom: 5px;
  text-transform: capitalize;
}
.info2-flavor-tip-rule {
  height: 1px; background: rgba(255,255,255,0.14);
  margin: 4px -2px 6px;
}
.info2-flavor-tip-row {
  display: flex; justify-content: space-between; align-items: baseline;
  gap: 12px; padding: 1px 0;
}
.info2-flavor-tip-src { opacity: 0.78; }
.info2-flavor-tip-votes {
  font-variant-numeric: tabular-nums;
  opacity: 0.95; font-weight: 500;
}
.info2-flavor-tip-votes--none { opacity: 0.4; font-weight: 400; }
.info2-flavor-tip-foot {
  margin-top: 6px; padding-top: 5px;
  border-top: 1px solid rgba(255,255,255,0.14);
  font-size: 10.5px; opacity: 0.7;
  text-align: center;
  font-variant-numeric: tabular-nums;
}
/* Single-line summary variant (no border, no top padding) used when the
   tip shows ONLY the aggregate ("9 sources · 4,000 votes total"). */
.info2-flavor-tip-foot-only {
  font-size: 11px; opacity: 0.85;
  text-align: center;
  font-variant-numeric: tabular-nums;
  font-weight: 500;
}
/* Subtle hover lift, no cursor change. Earlier rule used cursor:help
   which made the browser show a question-mark cursor that read like a
   help icon instead of a vote-breakdown trigger. The tooltip itself is
   the affordance. */
.info2-flavor[data-flavor-name],
.vb-strain-inline-chip[data-flavor-name] {
  cursor: pointer;
  transition: transform 0.12s ease, opacity 0.12s ease;
}
.info2-flavor[data-flavor-name]:hover,
.vb-strain-inline-chip[data-flavor-name]:hover {
  transform: translateY(-1px);
}

.info2-faq { display: flex; flex-direction: column; gap: 6px; }
.info2-faq-item { background: #F5F5F7; border-radius: 10px; overflow: hidden; }
.info2-faq-item summary {
  list-style: none; cursor: pointer; padding: 11px 14px;
  font-size: 13px; font-weight: 500; color: #1D1D1F;
  display: flex; justify-content: space-between; align-items: center; gap: 12px;
}
.info2-faq-item summary::-webkit-details-marker { display: none; }
.info2-faq-item summary svg { color: #86868B; transition: transform 0.2s; flex-shrink: 0; }
.info2-faq-item[open] summary svg { transform: rotate(180deg); }
.info2-faq-item p {
  padding: 0 14px 12px; font-size: 12.5px; color: #6E6E73; line-height: 1.5;
  margin: 0;
}

.info2-disclaimers { margin-top: 20px; padding-top: 14px; border-top: 1px solid rgba(0,0,0,0.08); }
.info2-disclaimers p { font-size: 11.5px; color: #6E6E73; line-height: 1.5; margin: 0; }
.info2-disclaimers p strong { color: #1D1D1F; font-weight: 600; }
.info2-dis-medical {
  background: rgba(245,158,11,0.08); border: 1px solid rgba(245,158,11,0.25);
  border-radius: 6px; padding: 10px 12px; margin-bottom: 8px;
  color: #78350f;
}
.info2-dis-medical strong { color: #78350f; }
.info2-dis-prop { margin-top: 6px; font-size: 10.5px; color: #86868B; }

/* ─── Helpful / Not helpful feedback row ───────────────────────────────
 * Apple-strict: monochrome icons, neutral tones, subtle hover. Icons are
 * functional (accessibility) so SVG fill/stroke uses currentColor only —
 * no decorative color anywhere in this block.
 */
.info2-feedback {
  margin-top: 16px; padding-top: 14px;
  border-top: 1px solid rgba(0,0,0,0.08);
  display: flex; align-items: center; gap: 12px;
  flex-wrap: wrap;
}
.info2-feedback-label {
  font-size: 12px; color: #6E6E73; font-weight: 500;
}
.info2-feedback-buttons {
  display: inline-flex; gap: 6px;
}
.info2-fb-btn {
  display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px;
  padding: 0; border-radius: 6px;
  background: transparent; border: 1px solid rgba(0,0,0,0.12);
  color: #6E6E73; cursor: pointer;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
}
.info2-fb-btn:hover { background: rgba(0,0,0,0.04); color: #1D1D1F; border-color: rgba(0,0,0,0.2); }
.info2-fb-btn:focus { outline: none; box-shadow: 0 0 0 2px rgba(0,0,0,0.12); }
.info2-fb-btn[disabled] { cursor: default; opacity: 0.6; }
.info2-fb-btn svg { display: block; }
.info2-feedback-thanks {
  font-size: 12px; color: #6E6E73;
}
.info2-feedback-thanks[data-state="hidden"] { display: none; }
.info2-feedback[data-voted="1"] .info2-feedback-buttons { display: none; }
.info2-feedback[data-voted="1"] .info2-feedback-label { display: none; }
.info2-feedback[data-voted="1"] .info2-feedback-thanks { display: inline; }

/* ─────────────────────────────────────────────────────────────────────
 * Store-overlay tags (May 2026). Render ONLY when the product has its
 * own store-side data (POS terpene lab values, POS effects/flavors list,
 * POS cannabis_type) so we can mark which universal entries the store
 * confirmed for THIS specific batch. Backwards-compatible: when the
 * overlay is empty, none of these classes are applied and the popup
 * renders identically to the pre-overlay design.
 *
 * Patterns (mirrors the v4 mockup at strain-info-overlay-mockup-v4.html):
 *  - .info2-type-store-says        — "Store says: HYBRID_SATIVA" caption
 *                                     under the type-consensus split meter
 *  - .info2-effect-store-confirmed — small green dot prefix on radar effect
 *                                     labels the store also reports
 *  - .info2-effects-legend         — one-liner under the radar explaining dot
 *  - .info2-effects-extras         — sentence listing any store-only extras
 *  - .info2-flavor.store-tagged    — coral-colored underline + ✓ on flavor
 *                                     chips the store confirms
 *  - .info2-flavor.store-only-extra — dashed-orange chip for store-only flavors
 *  - .info2-terp-pct .store-pct    — green "your batch" pill on terpene cards
 *  - .info2-terp-card.store-only-extra — dashed-orange terpene card for
 *                                         store-only terpenes
 *  - .info2-source-line            — italic muted attribution sentence
 *                                     under each section
 * Aroma section gets NO overlay — store has no aroma data.
 * ─────────────────────────────────────────────────────────────────── */
/* Repositioned May 2026: was a left-aligned flex row underneath the
 * title block; now a centered block that sits DIRECTLY below the type
 * pill inside .info2-title. No top border (the title block doesn't have
 * any separators above) — just a small gap. */
.info2-type-store-says {
  display: block;
  text-align: center;
  margin: 6px auto 0;
  font-size: 10.5px; color: #86868B;
  letter-spacing: 0.04em;
}
.info2-type-store-says-lbl {
  text-transform: uppercase; font-weight: 600; letter-spacing: 0.08em;
  color: #86868B;
  margin-right: 6px;
}
.info2-type-store-says-val {
  font-family: "SF Pro Display", "Inter", sans-serif;
  font-weight: 600; color: #1D1D1F;
  font-size: 11px; letter-spacing: 0.02em;
}

.info2-effect-store-confirmed {
  fill: #2E8B30;
  font-size: 11px;
}
.info2-effects-legend {
  margin-top: 10px;
  font-size: 11px; color: #86868B;
  text-align: center; line-height: 1.4;
  font-style: italic;
}
.info2-effects-extras {
  margin-top: 4px;
  font-size: 11.5px; color: #1D1D1F;
  text-align: center; line-height: 1.4;
}
.info2-effects-extras strong {
  color: #2E8B30; font-weight: 600;
}

.info2-flavor.store-tagged {
  position: relative;
  text-decoration: underline;
  text-decoration-color: rgba(46,139,48,0.55);
  text-decoration-thickness: 1.5px;
  text-underline-offset: 4px;
  text-decoration-skip-ink: none;
}
.info2-flavor.store-tagged::after {
  content: "\\2713";
  display: inline-block;
  margin-left: 4px;
  font-size: 0.55em;
  color: #2E8B30;
  font-weight: 700;
  vertical-align: middle;
  opacity: 0.85;
}
.info2-flavor.store-only-extra {
  color: #C2410C;
  border: 1.5px dashed #F97316;
  border-radius: 6px;
  padding: 1px 8px 1px 5px;
  background: rgba(249,115,22,0.05);
}
.info2-flavor.store-only-extra::before {
  content: "+ ";
  font-weight: 700;
  color: #C2410C;
}

.info2-terp-pct .store-pct {
  display: inline-flex; align-items: baseline; gap: 6px;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 999px;
  background: rgba(92,179,53,0.12);
  border: 1px solid rgba(92,179,53,0.35);
  font-size: 11px;
  color: #2E8B30; font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.info2-terp-pct .store-pct em {
  font-style: normal;
  color: #86868B; font-weight: 400; font-size: 10px;
  letter-spacing: 0.04em; text-transform: uppercase;
}
.info2-terp-card.store-only-extra {
  border: 1.5px dashed #F97316;
  background: rgba(249,115,22,0.04);
}
.info2-terp-card.store-only-extra .info2-terp-pct {
  color: #C2410C;
}
.info2-terp-card.store-only-extra .info2-terp-pct .store-pct {
  background: rgba(249,115,22,0.1);
  border: 1px dashed #F97316;
  color: #C2410C;
}
.info2-terp-card.store-only-extra .info2-terp-name::before {
  content: "+ ";
  color: #C2410C; font-weight: 700;
}

.info2-source-line {
  margin-top: 8px;
  font-size: 11px; color: #86868B;
  line-height: 1.5; font-style: italic;
  padding: 0 2px;
  text-align: center;
}
.info2-source-line strong {
  color: #2E8B30; font-weight: 600; font-style: normal;
}
.info2-source-line em {
  color: #C2410C; font-weight: 600; font-style: normal;
}

/* ─────────────────────────────────────────────────────────────────────
 * Dual-strain TAB pattern (May 2026, v3). When the popup represents a
 * cross — e.g. Baked Alaska × Georgia Pie — the API returns primary
 * plus secondary objects and we render ONE strain at a time, with a
 * tab strip at the top to swap. Replaces the previous side-by-side
 * dual-grid layout (commit 7b81c70).
 *
 * Three surfaces use the same .info2-strain-tabs control:
 *   1. Stage 1 — pill row directly in the product card (.teaser-pill)
 *      replaces the single strain link when the product is a cross.
 *   2. Stage 2 — compact inline preview (.vb-strain-inline) shows the
 *      tab strip above a compact body (no orbital).
 *   3. Stage 3 — full overlay (.vb-strain-overlay) shows the tab strip
 *      above the full body with orbital, FAQ, feedback.
 *
 * Apple-strict: segmented-control tabs (white pill on light gray
 * track, hairline border, 999px radius); muted italic caption naming
 * the cross. Single-strain products keep the legacy flat layout.
 * ─────────────────────────────────────────────────────────────────── */

/* Stage 1 — teaser pill row (in product card). Modeled after
 * .info2-orbit-chip + .vb-strain-inline-chip — small green outlined
 * pills. Active pill matches the active tab in stages 2 / 3. */
.teaser-pill-row {
  display: flex; flex-wrap: wrap; gap: 8px;
  padding: 12px 0 0;
  margin-top: 10px;
  border-top: 1px dashed rgba(0,0,0,0.08);
}
.teaser-pill-label {
  font-size: 10.5px; font-weight: 600; color: #86868B;
  text-transform: uppercase; letter-spacing: 0.08em;
  display: block;
  margin: 4px 0 4px;
  width: 100%;
}
.teaser-pill {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 5px 11px; border-radius: 999px;
  background: rgba(92,179,53,0.08); color: #2E8B30;
  border: 1px solid rgba(92,179,53,0.35);
  font: 500 12px/1.2 "SF Pro Text", "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}
.teaser-pill:hover { background: rgba(92,179,53,0.14); border-color: rgba(92,179,53,0.5); }
.teaser-pill.active { background: #2E8B30; color: #fff; border-color: #2E8B30; }
.teaser-pill::before {
  content: "";
  width: 6px; height: 6px; border-radius: 999px;
  background: currentColor; opacity: 0.7;
  display: inline-block;
}

/* Stages 2 + 3 — segmented-control tab strip above the strain body.
 * Hairline border, neutral fill, active tab is white pill. */
.info2-strain-tabs-wrap {
  display: flex; justify-content: center;
  margin: 14px 0 8px;
}
.info2-strain-tabs {
  display: inline-flex;
  background: rgba(0,0,0,0.04);
  border: 1px solid rgba(0,0,0,0.08);
  border-radius: 999px;
  padding: 3px;
  margin: 0 auto 4px;
  gap: 0;
  font-family: inherit;
}
.info2-strain-tab {
  background: transparent; border: none;
  padding: 6px 18px; border-radius: 999px;
  font: 500 12.5px/1.2 inherit;
  color: #6E6E73;
  cursor: pointer;
  letter-spacing: 0.01em;
  transition: background 0.18s, color 0.18s;
  white-space: nowrap;
}
.info2-strain-tab:hover { color: #1D1D1F; }
.info2-strain-tab.active {
  background: #fff;
  color: #1D1D1F;
  font-weight: 600;
  box-shadow: 0 1px 2px rgba(0,0,0,0.06), 0 0 0 0.5px rgba(0,0,0,0.04);
}
.info2-strain-tabs-caption {
  font-size: 11.5px; color: #86868B; font-style: italic;
  text-align: center;
  margin: 0 0 14px;
}
.info2-strain-tabs-caption strong { color: #1D1D1F; font-weight: 600; }

/* Compact lineage chip list — used when _renderInfographic is called
 * with compactLineage:true (kept for potential reuse; tab pattern
 * defaults to hideOrbital:true in stage 2). */
.info2-mini-lineage { display: flex; flex-direction: column; gap: 10px; }
.info2-mini-lineage-row { display: flex; flex-direction: column; gap: 6px; }
.info2-mini-lineage-label {
  font-size: 10px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.1em;
  color: #86868B;
}
.info2-mini-lineage-chips {
  display: flex; flex-wrap: wrap; gap: 5px 6px;
}
.info2-mini-lineage-chip {
  display: inline-block;
  padding: 3px 9px; border-radius: 999px;
  background: #fff; border: 1px solid rgba(0,0,0,0.12);
  font-size: 11px; color: #1D1D1F;
  white-space: nowrap;
}
.info2-mini-lineage-empty {
  font-size: 11px; color: #86868B; font-style: italic;
}

/* ─────────────────────────────────────────────────────────────────────
 * Phone-size adjustments (≤480px). The desktop layout assumes ~440px of
 * popup width; iframe widget sits inside ~360-400px on a phone, which
 * pinches the right-aligned chips/values and clips them. These rules
 * only loosen widths/wrap behavior; they don't touch colors, fonts, or
 * desktop layout. Surgical, additive, no selector overrides.
 * ─────────────────────────────────────────────────────────────────── */
@media (max-width: 480px) {
  /* Tighten the body gutter so right-aligned metadata isn't clipped by
   * the 14px right padding. */
  .info2-body { padding-left: 10px; padding-right: 10px; }

  /* Stats row — was "1.6fr auto 1fr" which gave the right "Popularity"
   * column ~110px, too tight for "4,000 reviews" right-aligned. Drop
   * the middle vertical divider and let the row wrap to two stacked
   * blocks at phone width. Reduce inner padding too. */
  .info2-stats-row {
    grid-template-columns: 1fr 1fr;
    padding: 12px 12px;
    gap: 8px;
  }
  .info2-stats-divider { display: none; }
  /* When divider is hidden the .info2-pop-block becomes the 2nd column.
   * Keep it right-aligned but allow its contents to shrink. */
  .info2-pop-block { min-width: 0; }
  .info2-pop-meta { white-space: nowrap; }

  /* Type-consensus legend — has a "of N sources" total pushed via
   * margin-left:auto. Allow wrap so it drops below the legend chips
   * instead of clipping past the right edge. */
  .info2-type-legend { flex-wrap: wrap; row-gap: 4px; }
  .info2-type-legend-total { margin-left: 0; }

  /* "May help" / "Watch for" split — at 1fr 1fr the inner columns are
   * ~150px on a phone, which clips the right-aligned vote count and
   * source-count line. Stack the two sections vertically instead. */
  .info2-split { grid-template-columns: 1fr; gap: 18px; }

  /* Rich-stack rows inside May help / Watch for — explicit min-width
   * 0 lets the name truncate gracefully and the votes chip stay flush
   * right instead of being pushed off the edge. */
  .info2-rich-top { gap: 8px; }
  .info2-rich-name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .info2-rich-votes { flex-shrink: 0; }
  /* Source-count line ("3/5 sources") can wrap normally; the inline
   * bars + text fits comfortably in one row at full width. */
  .info2-rich-sources { flex-wrap: wrap; }

  /* Orbital breadcrumb was 0 22px 8px — pull the side padding in so
   * "Parents › Grandparents › …" doesn't hit the right edge. */
  .info2-orbit-breadcrumb { padding-left: 14px; padding-right: 14px; }

  /* FAQ summary — has a right-aligned chevron at the end of a flex row.
   * Make sure the title can wrap so long FAQ questions don't push the
   * chevron off-screen. */
  .info2-faq-item summary { gap: 8px; }

  /* Terpene-card header — name + percentage chip in a flex row with
   * margin-left:auto on the percent. Allow wrap on very narrow widths
   * so the "store batch" pill doesn't clip when present. */
  .info2-terp-card-hdr { flex-wrap: wrap; row-gap: 4px; }
}
`;

let _stylesInjected = false;
export function ensureStrainOverlayStyles() {
  if (_stylesInjected) return;
  const style = document.createElement('style');
  style.textContent = OVERLAY_CSS;
  style.id = 'vb-strain-overlay-styles';
  document.head.appendChild(style);
  _stylesInjected = true;
}

// Terpene family keys. Matched against terpene name (alpha-/beta- stripped).
const _TERPENE_FAMILIES = [
  'caryophyllene', 'myrcene', 'limonene', 'pinene', 'linalool',
  'humulene', 'terpinolene', 'ocimene', 'bisabolol', 'nerolidol',
];
function _terpFamily(name) {
  if (!name) return 'default';
  const norm = String(name).toLowerCase()
    .replace(/^(alpha|beta|α|β)[-\s]*/, '')
    .trim();
  for (const key of _TERPENE_FAMILIES) {
    if (norm === key || norm.includes(key)) return key;
  }
  return 'default';
}
// Sensible defaults for aroma/taste/effect per terpene family — used when
// the source didn't provide them (most public databases don't).
const _TERPENE_DEFAULTS = {
  caryophyllene: { aroma: 'Peppery', taste: 'Black pepper, spice, wood', effect: 'Calm, anti-stress' },
  myrcene:       { aroma: 'Earthy',  taste: 'Musky, clove, ripe mango',  effect: 'Sedating, relaxing' },
  limonene:      { aroma: 'Citrus',  taste: 'Lemon zest, sweet citrus',  effect: 'Uplifting, mood-lifting' },
  pinene:        { aroma: 'Pine',    taste: 'Fresh pine, rosemary',      effect: 'Alert, focus, memory' },
  linalool:      { aroma: 'Floral',  taste: 'Lavender, soft florals',    effect: 'Relaxing, anti-anxiety' },
  humulene:      { aroma: 'Hoppy',   taste: 'Hops, earthy, herbaceous',  effect: 'Appetite-suppressant' },
  terpinolene:   { aroma: 'Fresh',   taste: 'Apple, lilac, cumin',       effect: 'Uplifting, energizing' },
  ocimene:       { aroma: 'Sweet',   taste: 'Mango, parsley, basil',     effect: 'Uplifting, decongestant' },
  bisabolol:     { aroma: 'Floral',  taste: 'Chamomile, honey',          effect: 'Soothing, anti-inflammatory' },
  nerolidol:     { aroma: 'Woody',   taste: 'Bark, tree, citrus',        effect: 'Sedating, skin-soothing' },
  default:       { aroma: '—',       taste: '—',                         effect: '—' },
};

// Names that scrapers occasionally pull into "parents" or "children"
// arrays but are NOT actual strain names — effect words, generic
// adjectives, or scraper placeholders. Filtered before display so
// the orbital doesn't show "Couch lock" as Sour Diesel's parent.
// Lowercase comparison; matches whole name only (after _stripHtml).
const _NON_STRAIN_NAMES = new Set([
  // Effect / sensation words AllBud's parser sometimes catches
  'couch lock', 'couchlock', 'couch-lock',
  'relaxed', 'relaxing', 'relaxation',
  'happy', 'euphoric', 'sleepy', 'creative', 'energetic', 'focused',
  'uplifted', 'aroused', 'talkative', 'tingly', 'hungry', 'giggly',
  // Common medical / side-effect words
  'pain relief', 'anxiety', 'depression', 'stress', 'insomnia', 'fatigue',
  'dry mouth', 'dry eyes', 'dizziness', 'paranoia', 'headache',
  // SeedFinder placeholders
  'unknown', 'unknown strain', 'unknown hybrid', 'unknown indica', 'unknown sativa',
  'probably', 'probably probably',
  // Generic words / artifacts
  'strain', 'hybrid', 'sativa', 'indica', 'cannabis', 'weed',
  'feminized', 'autoflower', 'regular',
]);

function _isPolluted(name) {
  if (!name) return true;
  const s = String(name).trim().toLowerCase();
  if (!s || s.length < 2) return true;
  // SeedFinder "probably:" prefix means "we don't know"
  if (s.startsWith('probably:') || s.startsWith('probably ')) return true;
  // Anything containing a colon is almost certainly a scrape artifact
  // (e.g., "probably: Probably", "unknown: Unknown")
  if (s.includes(':')) return true;
  if (_NON_STRAIN_NAMES.has(s)) return true;
  return false;
}

function _escape(s) {
  return String(s || '').replace(/[&<>"']/g, ch => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]
  ));
}

function _stripHtml(s) {
  return String(s || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

function _typeClass(type) {
  const t = (type || '').toLowerCase();
  if (t.includes('sativa')) return 'sativa';
  if (t.includes('indica')) return 'indica';
  return 'hybrid';
}
function _prettyType(type) {
  if (!type) return 'Hybrid';
  const t = String(type).toLowerCase().replace(/_/g, '-');
  if (t.includes('sativa-dominant') || t === 'sativa_dominant') return 'Sativa-dominant';
  if (t.includes('indica-dominant') || t === 'indica_dominant') return 'Indica-dominant';
  if (t === 'sativa') return 'Sativa';
  if (t === 'indica') return 'Indica';
  return 'Hybrid';
}

// ── Data shape adapter ──────────────────────────────────────────────
// Maps our /strain-info/ payload to the Infographic v2 design's shape.
function _adaptStrainData(data, productName) {
  // Prefer the clean canonical strain name when the catalog row supplies one.
  // For dual-strain crosses the top-level `name` field is stamped with the
  // full product_name ("SOUR DIESEL X DURBAN POISON | 0.5G | 2PK") which is
  // wrong for the tab strip and hero h1 — both should read "Sour Diesel".
  // Single-strain rows still resolve via productName-fallback if neither
  // canonical_name nor name is set.
  const name = data.canonical_name || data.name || productName || 'Strain';
  const thc = data.thc_range || (data.mega_thc_range) || '';
  const cbd = data.cbd_range || (data.mega_cbd_range) || '';
  // Prefer the arbitrated description_polished when available and
  // confidence label isn't Low. Falls back to the raw multi-source
  // description otherwise. The widget also surfaces arbitration_confidence_label
  // downstream so the UI can render "Verified" / "Reviewed" chips.
  const arbConfLabel = data.arbitration_confidence_label || null;
  const polished = data.description_polished || '';
  const description = (polished && arbConfLabel !== 'Low')
    ? polished
    : (data.description || '');
  // Merge cluster aliases (from arbitration) with mega/aliases arrays
  const clusterAliases = Array.isArray(data.arbitrated_cluster_aliases)
    ? data.arbitrated_cluster_aliases : [];
  const aliasCandidates = [
    ...clusterAliases,
    ...(Array.isArray(data.mega_aliases) ? data.mega_aliases : []),
    ...(Array.isArray(data.aliases) ? data.aliases : []),
  ];
  // Strip seed-bank artifacts from CUSTOMER-FACING aliases only (raw aliases
  // stay intact in the catalog). Drops trailing tokens like "Seeds",
  // "Autoflower", "Auto", "Feminized"/"Fem", "Regular" — e.g. "Wedding Cake
  // Seeds" -> "Wedding Cake" (which then dedupes against the canonical name).
  const _SEEDBANK_TAIL = /[\s\-]+(seeds?|autoflowering|autoflower|auto|femini[sz]ed|fem|regular|reg)$/i;
  const _stripSeedBankJunk = (s) => {
    let out = s.trim();
    let prev;
    do { prev = out; out = out.replace(_SEEDBANK_TAIL, '').trim(); } while (out !== prev);
    return out;
  };
  const aliasSeen = new Set([name.trim().toLowerCase()]);
  const aliases = [];
  for (const a of aliasCandidates) {
    if (typeof a !== 'string') continue;
    const cleaned = _stripSeedBankJunk(a);
    const k = cleaned.toLowerCase();
    if (k && !aliasSeen.has(k)) { aliasSeen.add(k); aliases.push(cleaned); }
  }
  const sourceCount = typeof data.mega_source_count === 'number' ? data.mega_source_count : 0;

  // Effects — prefer mega_effects_agg (has source_count + total_votes)
  const effectsAgg = Array.isArray(data.mega_effects_agg) ? data.mega_effects_agg : [];
  const effects = effectsAgg.slice(0, 6).map(e => ({
    name: e.name,
    agreement: e.source_count || 1,
    of: sourceCount || 6,
    votes: e.total_votes || 0,
  }));
  // Fallback to flat effects[] if mega_effects_agg is empty.
  if (effects.length === 0 && Array.isArray(data.effects)) {
    for (const e of data.effects.slice(0, 6)) {
      const nm = typeof e === 'string' ? e : (e.name || '');
      if (nm) effects.push({ name: nm, agreement: 1, of: sourceCount || 1, votes: 0 });
    }
  }

  const flavorsAgg = Array.isArray(data.mega_flavors_agg) ? data.mega_flavors_agg : [];
  const flavors = flavorsAgg.slice(0, 8).map(f => ({
    name: f.name,
    agreement: f.source_count || 1,
    of: sourceCount || 6,
    totalVotes: f.total_votes || 0,
  }));
  // Aromas — only present if the API supplies mega_aromas_agg. Most
  // strains today don't, so this commonly stays empty (no UI noise).
  const aromasAgg = Array.isArray(data.mega_aromas_agg) ? data.mega_aromas_agg : [];
  const aromas = aromasAgg.slice(0, 8).map(a => ({
    name: a.name,
    agreement: a.source_count || 1,
    of: sourceCount || 6,
    totalVotes: a.total_votes || 0,
  }));
  // Per-source vote breakdown for hover tooltip on flavor / aroma pills.
  // Already anonymized to "Source 1/2/…" by the time it reaches the
  // widget (see enrichment_endpoints.py). Stored as raw {src: [{name,votes}]}
  // and indexed by lowercase flavor name at render-time.
  const flavorsPerSource = (data.mega_flavors_per_source && typeof data.mega_flavors_per_source === 'object')
    ? data.mega_flavors_per_source : {};
  const aromasPerSource = (data.mega_aromas_per_source && typeof data.mega_aromas_per_source === 'object')
    ? data.mega_aromas_per_source : {};

  const mayHelpAgg = Array.isArray(data.mega_helps_with_agg) ? data.mega_helps_with_agg : [];
  const mayHelp = mayHelpAgg.slice(0, 4).map(m => ({
    name: m.name,
    agreement: m.source_count || 1,
    of: sourceCount || 6,
    votes: m.total_votes || 0,
  }));

  const sideAgg = Array.isArray(data.mega_side_effects_agg) ? data.mega_side_effects_agg : [];
  const sideEffects = sideAgg.slice(0, 4).map(s => ({
    name: s.name,
    agreement: s.source_count || 1,
    of: sourceCount || 6,
    votes: s.total_votes || 0,
  }));

  // Terpenes — mega_terpenes has {name, percent, percent_max, dominance_rank, sources}.
  const terpsRaw = Array.isArray(data.mega_terpenes) ? data.mega_terpenes
                 : Array.isArray(data.terpenes) ? data.terpenes : [];
  const terpenes = terpsRaw.slice(0, 6).map(t => {
    const fam = _terpFamily(t.name);
    const def = _TERPENE_DEFAULTS[fam] || _TERPENE_DEFAULTS.default;
    const pctVal = (typeof t.percent === 'number' && t.percent > 0) ? t.percent : null;
    // Range support: when multiple sources reported lab values for this
    // terpene, the merger writes percent_max alongside percent so we can
    // show "0.4–0.7%" instead of a single number. percent_max is null
    // unless it's strictly greater than percent.
    let pctMaxVal = null;
    if (typeof t.percent_max === 'number' && pctVal != null && t.percent_max > pctVal) {
      pctMaxVal = t.percent_max;
    }
    return {
      name: t.name,
      pct: pctVal,
      pctMax: pctMaxVal,
      family: fam,
      sources: Array.isArray(t.sources) ? t.sources.length : (t.source_count || 1),
      of: sourceCount || 6,
      aroma: t.aroma || def.aroma,
      taste: t.taste || def.taste,
      effect: t.effect || def.effect,
    };
  });

  // Parents — filter out scraper artifacts (effect words, "probably:"
  // placeholders, generic terms) before they reach the orbital.
  const parents = Array.isArray(data.mega_parents)
    ? data.mega_parents
        .map(p => ({
          name: p.name || '',
          sources: Array.isArray(p.sources) ? p.sources.length : 0,
        }))
        .filter(p => !_isPolluted(p.name))
    : [];

  // Children / offspring — same filter
  const children = Array.isArray(data.mega_children)
    ? data.mega_children
        .map(c => c.name || '')
        .filter(name => name && !_isPolluted(name))
    : [];

  // Type consensus — from mega_variance.strain_type, else fallback to single value.
  let typeConsensus;
  const variance = data.mega_variance && typeof data.mega_variance === 'object' ? data.mega_variance : {};
  const typeVar = variance.strain_type;
  if (typeVar && typeVar.per_source && Object.keys(typeVar.per_source).length > 1) {
    const consensusType = typeVar.consensus || data.strain_type;
    let agree = 0, dissent = 0;
    const dissentVals = new Set();
    for (const v of Object.values(typeVar.per_source)) {
      if (v === consensusType) agree += 1;
      else { dissent += 1; dissentVals.add(v); }
    }
    typeConsensus = {
      label: _prettyType(consensusType),
      votes: agree,
      of: agree + dissent,
      dissent: _prettyType([...dissentVals][0] || 'other'),
      dissentVotes: dissent,
    };
  } else {
    typeConsensus = {
      label: _prettyType(data.strain_type),
      votes: 1, of: 1, dissent: '', dissentVotes: 0,
    };
  }

  // THC variance (per-source)
  const thcVar = variance.thc_range;
  let thcRows = null;
  if (thcVar && thcVar.per_source && Object.keys(thcVar.per_source).length > 0) {
    const consensusThc = thcVar.consensus || thc;
    thcRows = Object.entries(thcVar.per_source).map(([src, val], i) => ({
      src: 'Source ' + (i + 1),
      val: String(val),
      diff: String(val) !== String(consensusThc),
    }));
  }

  // FAQs — prefer mega.faqs, else ed.faqs
  const faqs = Array.isArray(data.faqs) ? data.faqs
             : Array.isArray(data.mega_faqs) ? data.mega_faqs
             : [];

  // Popularity signal — prefer top-level review_count (aggregated across all
  // sources that report it). Falls back to summed per-effect votes when no
  // review_count data is available (most scrapers don't populate review_count
  // or per-effect votes; only weedmaps populates votes; allbud/strain_db
  // populate review_count). Real Do Si Dos has 1826 reviews; using the
  // effect-vote signal alone reported 262.
  let totalVotes = Number(data.mega_review_count) || 0;
  const popSignalSource = totalVotes > 0 ? 'reviews' : 'votes';
  if (totalVotes === 0 && Array.isArray(data.mega_effects_agg)) {
    for (const e of data.mega_effects_agg) totalVotes += Number(e.total_votes) || 0;
  }
  // log10 scaling because review counts go 0 → 5000+ on famous strains;
  // log compresses that into a usable 0..~5 range. Denominator /6 (was /5)
  // because review_count distributions go higher than the old vote-only
  // signal — keeps the most-popular strains at 5 dots without compressing
  // every mid-popular strain to the cap.
  const voteComponent = Math.min(Math.log10(totalVotes + 1) / 6, 1); // 0..1
  const sourceComponent = Math.min(sourceCount / 7, 1);              // 0..1
  const popScore = voteComponent * 0.7 + sourceComponent * 0.3;      // 0..1
  // Tier thresholds calibrated against the multi-source cohort (source_count
  // >= 2) in the merged catalog (May 1 2026, N=12,779). Calibrated so that
  // dispensary inventory shows real differentiation rather than every
  // popular-store strain landing at Legendary.
  //   pop_score >= 0.78   → Legendary  (true Hall of Fame: ~5 strains catalog-wide:
  //                                     Blue Dream, OG Kush, Pineapple Express,
  //                                     Cereal Milk, Northern Lights)
  //   pop_score >= 0.62   → Iconic     (262 strains: Wedding Cake, Sour Diesel,
  //                                     Granddaddy Purple, GG4, GSC, Gelato, etc.)
  //   pop_score >= 0.50   → Popular    (930 strains: well-known but not iconic)
  //   pop_score >= 0.38   → Common     (3,084 strains: some recognition)
  //   else                 → Niche     (8,498 strains: obscure / new / breeder-only)
  // 5-dot system maps tier → dots so visual matches label exactly.
  const popTier = popScore >= 0.78 ? 'Legendary'
                : popScore >= 0.62 ? 'Iconic'
                : popScore >= 0.50 ? 'Popular'
                : popScore >= 0.38 ? 'Common'
                                   : 'Niche';
  const popDots = popTier === 'Legendary' ? 5
                : popTier === 'Iconic' ? 4
                : popTier === 'Popular' ? 3
                : popTier === 'Common' ? 2
                                       : 1;

  // #440 — real numeric rating for the Popularity row. Backend sends
  // mega_rating = {value, count}: a review-count-weighted average across
  // per-source ratings (0-5 scale), falling back to the catalog aggregate
  // rating_value. null/absent/invalid → popRating stays null and the
  // renderer keeps the legacy tier+dots display (never show "0.0").
  let popRating = null;
  const _mr = data.mega_rating;
  if (_mr && typeof _mr === 'object') {
    const _mrVal = Number(_mr.value);
    if (isFinite(_mrVal) && _mrVal > 0 && _mrVal <= 5) {
      const _mrCount = Number(_mr.count);
      popRating = {
        value: _mrVal,
        count: (isFinite(_mrCount) && _mrCount > 0) ? Math.round(_mrCount) : 0,
      };
    }
  }

  // Per-source descriptions — render WITHOUT labels per user: "hide every
  // source name". Just stack the differing blurbs. Visual separator does
  // the job of distinguishing them. Also run stripHtmlLite to catch any
  // lingering tag fragments the backend clean-pass missed.
  let descriptionsPerSource = [];
  const dps = data.mega_descriptions_per_source;
  if (dps && typeof dps === 'object') {
    const srcs = Object.keys(dps).sort();
    descriptionsPerSource = srcs
      .map(src => ({ text: _stripHtml(String(dps[src] || '').trim()) }))
      .filter(d => d.text.length > 40);
  }

  return {
    name,
    aliases: aliases.slice(0, 4),
    thc: thc || '—',
    cbd,
    description,
    descriptionsPerSource,
    typeConsensus,
    thcRows,
    effects,
    flavors,
    aromas,
    flavorsPerSource,
    aromasPerSource,
    mayHelp,
    sideEffects,
    terpenes,
    parents,
    children,
    offspringCount: children.length,
    // Recursive 2-level family tree (parents.parents = grandparents,
    // children.children = grandchildren). Used by the orbital lineage SVG.
    // Shape: { name, ancestors:[{name, sources, ancestors:[…]}], offspring:[…] }
    familyTree: (data.mega_family_tree && typeof data.mega_family_tree === 'object')
      ? data.mega_family_tree : null,
    typeForOrbital: _prettyType(data.strain_type), // pre-computed for orbital center color
    faqs,
    sourceCount,
    popDots,
    popTier,
    popVotes: totalVotes,
    popSignalSource, // 'reviews' (preferred) | 'votes' (fallback when no review_count data)
    popRating, // {value, count} weighted numeric rating | null (no rating data)
    // Arbitration overlay — widget renderer can show a confidence chip
    // ("Reviewed" / "High confidence") next to the description when these
    // are present. Backend fills these from universal_strain_catalog
    // arbitration columns after the Pass 4 reconciler runs.
    arbitrationConfidence: typeof data.arbitration_confidence === 'number' ? data.arbitration_confidence : null,
    arbitrationLabel: arbConfLabel, // 'High' / 'Medium' / 'Low' / null
    arbitratedCanonicalName: data.arbitrated_canonical_name || null,
    usingPolishedDescription: !!(polished && arbConfLabel !== 'Low'),
    // Store-side overlay: POS-supplied data for THIS specific product / batch
    // (cannabis_type, effects, flavors, terpenes-with-percent). Backend
    // builds it from products.product_meta_data — see
    // enrichment_endpoints.get_strain_info. Empty {} when the store has
    // no lab/POS data, in which case overlay UI is suppressed.
    storeOverlay: (data.store_overlay && typeof data.store_overlay === 'object')
      ? {
          strain_type: typeof data.store_overlay.strain_type === 'string' && data.store_overlay.strain_type
            ? data.store_overlay.strain_type : null,
          effects: Array.isArray(data.store_overlay.effects) ? data.store_overlay.effects : [],
          flavors: Array.isArray(data.store_overlay.flavors) ? data.store_overlay.flavors : [],
          terpenes: (data.store_overlay.terpenes && typeof data.store_overlay.terpenes === 'object')
            ? data.store_overlay.terpenes : {},
        }
      : null,
    // Terpene comparison view: catalog (universal sources) vs store POS batch.
    // Backend builds this in _diff_terpenes() — see enrichment_endpoints.py.
    // Shape (one row per terpene):
    //   { name, store_percent|null, catalog_percent|null, catalog_percent_max|null,
    //     catalog_source_count, status: 'both' | 'store_only' | 'catalog_only' }
    // Sorted: both → store_only → catalog_only. Empty array when backend
    // omits the field (older deploys / no catalog + no batch) — widget then
    // falls back to the existing terpene render path.
    terpeneComparison: Array.isArray(data.terpene_comparison) ? data.terpene_comparison : [],
  };
}

// ── Radar chart SVG ─────────────────────────────────────────────────
function _renderRadar(effects, storeEffects, stackLabels) {
  if (!effects || effects.length === 0) {
    return '<div class="info2-radar-empty">No effect data reported</div>';
  }
  // Build a case-insensitive lookup so we can mark each radar label with a
  // small green dot when the store also confirms that effect for THIS batch.
  // storeEffects is a string[] (or null/undefined when no overlay).
  const _storeSet = new Set();
  if (Array.isArray(storeEffects)) {
    for (const x of storeEffects) {
      if (typeof x === 'string' && x.trim()) _storeSet.add(x.trim().toLowerCase());
    }
  }
  const size = 300, cx = size / 2, cy = size / 2, rMax = 80;
  const N = effects.length;
  // Vote-aware radial scaling. Old version used only source agreement
  // (agreement / of) so a 366-vote "Relaxed" landed at the same
  // radius as a 12-vote "Sleepy" if both had the same source coverage.
  // New scaling blends 60% vote weight + 40% source agreement so the
  // top-voted effect pins to the outer ring and weaker ones pull
  // inward — matches the user's mental model of "this strain is
  // overwhelmingly Relaxed and lightly Creative".
  const maxVotes = Math.max(1,
    ...effects.map(e => Number(e.votes) || 0));
  const points = effects.map((e, i) => {
    const angle = -Math.PI / 2 + (i / N) * Math.PI * 2;
    const sourceRatio = (e.agreement || 0) / Math.max(e.of, 1);   // 0..1
    const voteRatio = (Number(e.votes) || 0) / maxVotes;          // 0..1
    // If no votes data anywhere, fall back to pure source-agreement.
    const blend = maxVotes > 1 ? (voteRatio * 0.6 + sourceRatio * 0.4) : sourceRatio;
    // Floor at 0.18 so even a single low-vote effect is visible
    // (otherwise nodes collapse onto the center dot).
    const rVal = Math.max(blend, 0.18) * rMax;
    const x = cx + Math.cos(angle) * rVal;
    const y = cy + Math.sin(angle) * rVal;
    const lx = cx + Math.cos(angle) * (rMax + 26);
    const ly = cy + Math.sin(angle) * (rMax + 26);
    return { x, y, angle, lx, ly, ...e };
  });
  const pathD = points.map((p, i) => (i === 0 ? 'M ' : 'L ') + p.x + ' ' + p.y).join(' ') + ' Z';
  const rings = [0.33, 0.66, 1].map(f => f * rMax);

  let svg = '<svg viewBox="0 0 ' + size + ' ' + size + '" class="info2-radar" width="100%" preserveAspectRatio="xMidYMid meet">';
  for (const r of rings) {
    svg += '<circle cx="' + cx + '" cy="' + cy + '" r="' + r + '" fill="none" stroke="rgba(0,0,0,0.06)"/>';
  }
  for (const p of points) {
    svg += '<line x1="' + cx + '" y1="' + cy + '" x2="' + (cx + Math.cos(p.angle) * rMax) + '" y2="' + (cy + Math.sin(p.angle) * rMax) + '" stroke="rgba(0,0,0,0.05)"/>';
  }
  svg += '<path d="' + pathD + '" fill="rgba(92,179,53,0.22)" stroke="#5CB335" stroke-width="1.5" stroke-linejoin="round"/>';
  for (const p of points) {
    svg += '<circle cx="' + p.x + '" cy="' + p.y + '" r="3" fill="#5CB335" stroke="#fff" stroke-width="1.5"/>';
  }
  for (const p of points) {
    const anchor = Math.abs(p.lx - cx) < 10 ? 'middle' : p.lx < cx ? 'end' : 'start';
    const _storeMatch = _storeSet.has(String(p.name || '').trim().toLowerCase());
    // When the store confirms this effect, prefix the label with a small
    // green-filled dot (●). Rendered as an SVG <tspan> so it stays
    // aligned to the label baseline. Plain <text> when no overlay match.
    if (_storeMatch) {
      svg += '<text x="' + p.lx + '" y="' + p.ly + '" text-anchor="' + anchor + '" font-size="12" font-weight="600" fill="#1D1D1F">' +
             '<tspan class="info2-effect-store-confirmed" fill="#2E8B30">●</tspan> ' +
             '<tspan>' + _escape(p.name) + '</tspan></text>';
    } else {
      svg += '<text x="' + p.lx + '" y="' + p.ly + '" text-anchor="' + anchor + '" font-size="12" font-weight="600" fill="#1D1D1F">' + _escape(p.name) + '</text>';
    }
    // §10 — STANDALONE MENU POPUP ONLY (gated on stackLabels). On phones the
    // single combined meta line ("1,356 votes · 20 sources") clips at the
    // radar's left/right edges; SVG <text> can't wrap via CSS, so the popup
    // splits it into two stacked, shorter lines. The in-chat (paid) chatbot
    // keeps the original single combined line (stackLabels falsy).
    if (stackLabels) {
      const votesLine   = p.votes ? (p.votes.toLocaleString() + ' votes') : null;
      const sourcesLine = p.agreement + (p.agreement === 1 ? ' source' : ' sources');
      if (votesLine) {
        svg += '<text x="' + p.lx + '" y="' + (p.ly + 12) + '" text-anchor="' + anchor + '" font-size="9.5" fill="#86868B">' + _escape(votesLine) + '</text>';
        svg += '<text x="' + p.lx + '" y="' + (p.ly + 23) + '" text-anchor="' + anchor + '" font-size="9.5" fill="#86868B">' + _escape(sourcesLine) + '</text>';
      } else {
        svg += '<text x="' + p.lx + '" y="' + (p.ly + 12) + '" text-anchor="' + anchor + '" font-size="9.5" fill="#86868B">' + _escape(sourcesLine) + '</text>';
      }
    } else {
      const meta = p.votes ? (p.votes.toLocaleString() + ' votes · ' + p.agreement + (p.agreement === 1 ? ' source' : ' sources')) : (p.agreement + (p.agreement === 1 ? ' source' : ' sources'));
      svg += '<text x="' + p.lx + '" y="' + (p.ly + 12) + '" text-anchor="' + anchor + '" font-size="9.5" fill="#86868B">' + _escape(meta) + '</text>';
    }
  }
  svg += '</svg>';
  return svg;
}

// ── Filterable orbital lineage ─────────────────────────────────────
// Matches the reference design at C:\Users\meghv\Downloads\
// "Lineage E - Filterable Orbital _standalone_.html". Five generation
// rings, full 360° node distribution per ring, HTML pill nodes
// (positioned absolutely on .info2-orbit-stage), filter chips above
// to toggle each generation. Rings are dashed guide circles only —
// the "complete circle" the user mentioned. No buttons / no pointer
// cursor on nodes; cursor stays default. Tooltip shows on hover.
function _renderOrbital(strainName, parents, children, familyTree, centerType, thc) {
  // Extract 2nd-generation nodes from family_tree
  // (depth=3 backend gives us grandparents + grandchildren).
  const ft = familyTree && typeof familyTree === 'object' ? familyTree : {};
  const grandparents = [];
  if (Array.isArray(ft.ancestors)) {
    for (const p of ft.ancestors) {
      const gas = Array.isArray(p && p.ancestors) ? p.ancestors : [];
      for (const ga of gas) {
        if (ga && ga.name && !_isPolluted(ga.name)) {
          grandparents.push({ name: ga.name,
            sources: Array.isArray(ga.sources) ? ga.sources.length : 0 });
        }
      }
    }
  }
  const greatChildren = [];
  if (Array.isArray(ft.offspring)) {
    for (const c of ft.offspring) {
      const gcs = Array.isArray(c && c.offspring) ? c.offspring : [];
      for (const gc of gcs) {
        if (gc && gc.name && !_isPolluted(gc.name)) {
          greatChildren.push({ name: gc.name,
            sources: Array.isArray(gc.sources) ? gc.sources.length : 0 });
        }
      }
    }
  }

  // Generation buckets (ordered inner→outer to match reference radii).
  // Each ring distributes its nodes evenly around the FULL 360° circle.
  // Counts cap at the reference's per-ring slice limits.
  const tier1Parents = (parents || []).map(p => ({
    name: p.name || '', sources: typeof p.sources === 'number' ? p.sources : 0,
  })).filter(p => p.name);
  const tier1Children = (children || []).map(c => ({
    name: typeof c === 'string' ? c : (c && c.name) || '',
    sources: 0,
  })).filter(c => c.name);

  // Ring radii pushed OUT vs the reference (which used 15% / 25%) so
  // pills at the inner ring clear the center nucleus pill in our 440px
  // popup. Reference popup was 480px → we have 40px less, so pills
  // need a bit more breathing room from center. Outer rings same as ref.
  const gens = [
    { key: 'parents',     label: 'Parents',           color: '#5CB335',
      radiusPct: 23, defaultOn: true,  nodes: tier1Parents.slice(0, 6) },
    { key: 'children',    label: 'Children',          color: '#0EA5E9',
      radiusPct: 33, defaultOn: true,  nodes: tier1Children.slice(0, 8) },
    { key: 'gp',          label: 'Grandparents',      color: '#86868B',
      radiusPct: 41, defaultOn: false, nodes: grandparents.slice(0, 8) },
    { key: 'descendants', label: 'Later descendants', color: '#F97316',
      radiusPct: 47, defaultOn: false, nodes: greatChildren.slice(0, 12) },
  ];

  const hasAny = gens.some(g => g.nodes.length > 0);
  if (!hasAny) {
    // Dead-end strain — still render a `.info2-orbit-section` with a
    // breadcrumb so the user can navigate back via the breadcrumb the
    // outer recenterTo handler maintains. Without this, the user is
    // stranded on the dead-end with no path back to the prior strain.
    return '<div class="info2-orbit-section" data-current-name="' + _escape(strainName) + '">' +
           '<div class="info2-orbit-breadcrumb">' +
           '<span class="info2-orbit-breadcrumb-current">' + _escape(strainName) + '</span>' +
           '</div>' +
           '<div style="padding:48px 16px;text-align:center;font-size:13px;color:#86868B;font-style:italic;">Lineage data not available for this strain.<br/><span style="font-size:11px;color:#AEAEB2;">Click a name above to go back.</span></div>' +
           '</div>';
  }

  // Filter chips (only render for generations that actually have data)
  let chipsHtml = '<div class="info2-orbit-filters">';
  for (const g of gens) {
    if (g.nodes.length === 0) continue;
    chipsHtml += '<button class="info2-orbit-chip" data-gen="' + g.key + '"' +
                 ' data-on="' + (g.defaultOn ? 'true' : 'false') + '" type="button">' +
                 '<span class="info2-orbit-chip-dot" style="background:' + g.color + '"></span>' +
                 _escape(g.label) + ' · ' + g.nodes.length +
                 '</button>';
  }
  chipsHtml += '</div>';

  // SVG dashed guide rings — viewBox 0 0 400 400, one ring per generation
  // with non-rendering generations (defaultOn=false) starting hidden.
  let ringsHtml = '<svg class="info2-orbit-rings" viewBox="0 0 400 400" preserveAspectRatio="xMidYMid meet">';
  for (const g of gens) {
    if (g.nodes.length === 0) continue;
    const r = (g.radiusPct / 100) * 400;
    ringsHtml += '<circle cx="200" cy="200" r="' + r +
                 '" stroke="' + g.color + '"' +
                 ' data-gen="' + g.key + '"' +
                 ' data-hidden="' + (g.defaultOn ? 'false' : 'true') + '"/>';
  }
  ringsHtml += '</svg>';

  // Center pill — strain name only. The type label (Indica-Dominant /
  // Sativa-Dominant) used to be in the subtitle along with THC; both
  // were removed because (a) the type already shows as the colored pill
  // up top, (b) THC is hidden from the customer-facing widget anyway,
  // and (c) the user wanted the center to be clean and unambiguous.
  const centerLabel = String(strainName).length > 22
    ? String(strainName).slice(0, 22) + '…' : strainName;
  const nucleusHtml =
    '<div class="info2-orbit-nucleus">' +
    _escape(centerLabel) +
    '</div>';

  // Pill nodes — positioned absolutely via inline left%/top%, ring-by-ring,
  // FULL 360° distribution starting at 12 o'clock (-π/2). Same formula as
  // the reference: `(i / N) * 2π - π/2`.
  //
  // V3 (May 2026): pill SIZE is UNIFORM within each generation; the
  // visual weight comes from a per-node halo (box-shadow scaled by
  // source_count via data-tier) and a small count badge top-right. The
  // halo encoding makes high-confidence parents pop without making the
  // layout jump as pills change size. Tier mapping is 1-indexed because
  // data-tier="0" hides the node (source_count 0 = unconfirmed):
  //   0  →  0 sources   → hidden
  //   1  →  1–4         → faint default shadow + gray badge
  //   2  →  5–19        → mid green halo + mid-green badge
  //   3  →  20+         → bold green halo + full-green badge
  const _tierFor = (n) => {
    const c = (n && typeof n.sources === 'number') ? n.sources : 0;
    if (c <= 0) return 1;       // backfill to tier 1 when source-count
                                // unknown so the node still renders
                                // (some scrapers don't report counts).
                                // Use 1 (faint) rather than 0 (hide).
    if (c >= 20) return 3;
    if (c >= 5)  return 2;
    return 1;
  };
  let nodesHtml = '';
  for (const g of gens) {
    if (g.nodes.length === 0) continue;
    const N = g.nodes.length;
    for (let i = 0; i < N; i++) {
      const node = g.nodes[i];
      const angle = (i / N) * Math.PI * 2 - Math.PI / 2;
      const left = 50 + Math.cos(angle) * g.radiusPct;
      const top = 50 + Math.sin(angle) * g.radiusPct;
      const short = String(node.name).length > 14
        ? String(node.name).slice(0, 14) + '…' : node.name;
      const sourceCount = (node && typeof node.sources === 'number' && node.sources > 0)
        ? node.sources : 0;
      const meta = sourceCount > 0
        ? (sourceCount + (sourceCount === 1 ? ' source' : ' sources'))
        : '';
      const tier = _tierFor(node);
      // Base padding per generation (descendants / grandparents slightly
      // smaller baseline). NO popularity scaling — uniform within ring.
      const basePad = (g.key === 'descendants') ? '4px 9px'
                    : (g.key === 'gp')          ? '4px 9px'
                                                : '5px 10px';
      const baseFont = (g.key === 'descendants' || g.key === 'gp') ? 10 : 10.5;
      // Badge HTML — only emitted when we have a real positive count
      // to display (avoid showing "0" for nodes with unknown counts).
      const badgeHtml = sourceCount > 0
        ? '<span class="info2-orbit-node-badge">' + sourceCount + '</span>'
        : '';
      nodesHtml +=
        '<div class="info2-orbit-node"' +
        ' data-gen="' + g.key + '"' +
        ' data-tier="' + tier + '"' +
        ' data-name="' + _escape(node.name) + '"' +
        ' data-meta="' + _escape(meta) + '"' +
        ' data-hidden="' + (g.defaultOn ? 'false' : 'true') + '"' +
        ' style="left:' + left.toFixed(2) + '%;top:' + top.toFixed(2) + '%;' +
        'padding:' + basePad + ';' +
        'font-size:' + baseFont + 'px;' +
        '">' +
        _escape(short) +
        badgeHtml +
        '</div>';
    }
  }

  // Stage = breadcrumb (click history) + filters + ring SVG + nucleus +
  //         node pills + tooltip + loading veil (shown during recenter fetch)
  //         + legend caption (V3 halo encoding hint, May 2026)
  return '<div class="info2-orbit-section" data-current-name="' + _escape(strainName) + '">' +
         '<div class="info2-orbit-breadcrumb">' +
         '<span class="info2-orbit-breadcrumb-current">' + _escape(strainName) + '</span>' +
         '</div>' +
         chipsHtml +
         '<div class="info2-orbit-stage">' +
         ringsHtml +
         nucleusHtml +
         nodesHtml +
         '<div class="info2-orbit-tip" data-state="hidden"></div>' +
         '<div class="info2-orbit-loading" data-state="hidden">Loading lineage</div>' +
         '</div>' +
         '<div class="info2-orbit-legend">Halo size shows how many sources confirm each parent.</div>' +
         '</div>';
}

// (Legacy SVG orbital removed 2026-04-27 — replaced by the HTML-pill
// filterable orbital above which matches the reference design exactly.)
function _renderOrbital_LEGACY_UNUSED_OLD(strainName, parents, children, familyTree, centerType, totalSources) {
  const W = 440, H = 440;
  const cx = W / 2, cy = H / 2;
  const innerR = 95, outerR = 165;
  // Type → center color (semantic only, not decorative — distinguishes
  // sativa / indica / hybrid at a glance).
  const TYPE_COLORS = {
    'Sativa': '#5CB335', 'Indica': '#7C3AED', 'Hybrid': '#F59E0B',
    'Sativa-dominant': '#7CC55E', 'Indica-dominant': '#A78BFA',
  };
  const centerColor = TYPE_COLORS[centerType] || '#0A1B3D';

  // Pull deeper generations from family_tree if backend supplied it.
  // Shape: { ancestors:[{name, sources, ancestors:[…]}], offspring:[…] }
  const ft = familyTree && typeof familyTree === 'object' ? familyTree : {};
  const tier1Parents = (parents || []).map(p => ({
    name: p.name || '',
    sources: typeof p.sources === 'number' ? p.sources : 0,
  })).filter(p => p.name);
  const tier1Children = (children || []).map(name => ({
    name: typeof name === 'string' ? name : (name && name.name) || '',
    sources: 0,
  })).filter(c => c.name);

  // Tier 2 = grandparents + grandchildren extracted from family_tree
  const tier2Up = [];
  if (Array.isArray(ft.ancestors)) {
    for (const p of ft.ancestors) {
      const gas = Array.isArray(p && p.ancestors) ? p.ancestors : [];
      for (const ga of gas) {
        if (ga && ga.name) {
          tier2Up.push({ name: ga.name,
            sources: Array.isArray(ga.sources) ? ga.sources.length : 0 });
        }
      }
    }
  }
  const tier2Down = [];
  if (Array.isArray(ft.offspring)) {
    for (const c of ft.offspring) {
      const gcs = Array.isArray(c && c.offspring) ? c.offspring : [];
      for (const gc of gcs) {
        if (gc && gc.name) {
          tier2Down.push({ name: gc.name,
            sources: Array.isArray(gc.sources) ? gc.sources.length : 0 });
        }
      }
    }
  }

  const hasAny = tier1Parents.length || tier1Children.length || tier2Up.length || tier2Down.length;
  if (!hasAny) {
    return '<div style="padding:20px 0;text-align:center;font-size:12px;color:#86868B;font-style:italic;">Lineage data not available</div>';
  }

  // Source-confirmation visual encoding helpers
  const opacityFor = (sources) => sources >= 4 ? 1.0 : sources >= 2 ? 0.8 : 0.5;
  const radiusFor = (sources) => 8 + Math.min(sources, 5) * 2; // 8..18
  const isHub = (sources) => sources >= 4;

  // Place nodes on an arc — top arc for ancestors, bottom for descendants.
  // Returns array of {x, y, r, color, opacity, hub, name, sources}.
  // direction: 'up' (-90° base, spread upward) or 'down' (90° base, spread downward).
  const placeOnArc = (nodes, ringR, direction, color) => {
    if (!nodes.length) return [];
    const visible = nodes.slice(0, 5); // 5 visible per arc; rest collapse to "+N more"
    const baseAngle = direction === 'up' ? -Math.PI / 2 : Math.PI / 2;
    // Spread across 140° (2.44 rad) total arc, centered on baseAngle
    const arcSpan = visible.length === 1 ? 0 : 2.44;
    const start = baseAngle - arcSpan / 2;
    return visible.map((n, i) => {
      const t = visible.length === 1 ? 0 : i / (visible.length - 1);
      const angle = start + t * arcSpan;
      return {
        x: cx + Math.cos(angle) * ringR,
        y: cy + Math.sin(angle) * ringR,
        r: radiusFor(n.sources),
        color,
        opacity: opacityFor(n.sources),
        hub: isHub(n.sources),
        name: n.name,
        sources: n.sources,
        angle,
      };
    });
  };

  const innerUp = placeOnArc(tier1Parents, innerR, 'up', '#0A1B3D');     // navy = parents
  const innerDown = placeOnArc(tier1Children, innerR, 'down', '#5CB335'); // green = children
  const outerUp = placeOnArc(tier2Up, outerR, 'up', '#5C6275');          // muted navy = grandparents
  const outerDown = placeOnArc(tier2Down, outerR, 'down', '#7CC55E');     // muted green = grandchildren

  let svg = '<svg viewBox="0 0 ' + W + ' ' + H + '" class="info2-orbit-svg" width="100%" preserveAspectRatio="xMidYMid meet">';

  // Concentric guide rings (subtle, color-coded by generation distance —
  // matches the reference design at ~r=14/22/32/40 in 100×100, scaled here).
  svg += '<circle cx="' + cx + '" cy="' + cy + '" r="40" fill="none" stroke="rgba(92,179,53,0.18)" stroke-width="1"/>';
  svg += '<circle cx="' + cx + '" cy="' + cy + '" r="' + innerR + '" fill="none" stroke="rgba(14,165,233,0.16)" stroke-width="1"/>';
  svg += '<circle cx="' + cx + '" cy="' + cy + '" r="130" fill="none" stroke="rgba(249,115,22,0.10)" stroke-width="1"/>';
  svg += '<circle cx="' + cx + '" cy="' + cy + '" r="' + outerR + '" fill="none" stroke="rgba(134,134,139,0.18)" stroke-width="1"/>';

  // Edges — center → tier1 nodes
  for (const n of [...innerUp, ...innerDown]) {
    svg += '<line x1="' + cx + '" y1="' + cy + '" x2="' + n.x + '" y2="' + n.y +
           '" stroke="rgba(0,0,0,0.10)" stroke-width="1"/>';
  }
  // Tier1 → tier2: distribute outer nodes near their respective inner ancestor/descendant.
  // Simpler: draw straight from center for clarity (V1).
  for (const n of [...outerUp, ...outerDown]) {
    svg += '<line x1="' + cx + '" y1="' + cy + '" x2="' + n.x + '" y2="' + n.y +
           '" stroke="rgba(0,0,0,0.05)" stroke-width="0.8"/>';
  }

  // Center cluster: glow halo + main dot + ring matching the type color
  svg += '<circle cx="' + cx + '" cy="' + cy + '" r="38" fill="' + centerColor + '" opacity="0.10"/>';
  svg += '<circle cx="' + cx + '" cy="' + cy + '" r="28" fill="' + centerColor + '" opacity="0.18"/>';
  svg += '<circle cx="' + cx + '" cy="' + cy + '" r="22" fill="' + centerColor + '"/>';
  svg += '<text x="' + cx + '" y="' + (cy - 50) + '" text-anchor="middle" font-size="9.5" fill="#86868B" letter-spacing="1" font-weight="600">' + _escape(centerType.toUpperCase()) + '</text>';
  // Strain name below center (truncated if needed)
  const centerLabel = String(strainName).length > 22 ? String(strainName).slice(0, 22) + '…' : strainName;
  svg += '<text x="' + cx + '" y="' + (cy + 6) + '" text-anchor="middle" font-size="13" font-weight="700" fill="#fff">' + _escape(centerLabel) + '</text>';
  if (totalSources) {
    svg += '<text x="' + cx + '" y="' + (cy + 60) + '" text-anchor="middle" font-size="10" fill="#86868B">' + totalSources + ' sources</text>';
  }

  // Render nodes — corona first (under), then dot, then label
  const drawNode = (n) => {
    let s = '';
    if (n.hub) {
      // Most-descended halo (gold ring) — semantic, not decorative
      s += '<circle cx="' + n.x + '" cy="' + n.y + '" r="' + (n.r + 4) +
           '" fill="none" stroke="#FBBF24" stroke-width="1.5" opacity="0.85"/>';
    }
    s += '<circle class="info2-orbit-node" data-name="' + _escape(n.name) +
         '" data-sources="' + n.sources + '"' +
         ' cx="' + n.x + '" cy="' + n.y + '" r="' + n.r +
         '" fill="' + n.color + '" opacity="' + n.opacity + '"/>';
    // Position label outside the ring (radially outward)
    const labelOffset = n.r + 7;
    const lx = n.x + Math.cos(n.angle) * labelOffset;
    const ly = n.y + Math.sin(n.angle) * labelOffset;
    const anchor = Math.abs(lx - cx) < 8 ? 'middle' : (lx < cx ? 'end' : 'start');
    const short = String(n.name).length > 14 ? String(n.name).slice(0, 14) + '…' : n.name;
    s += '<text x="' + lx + '" y="' + (ly + 3) + '" text-anchor="' + anchor +
         '" font-size="10" font-weight="500" fill="#1D1D1F" pointer-events="none">' + _escape(short) + '</text>';
    return s;
  };
  for (const n of [...innerUp, ...innerDown, ...outerUp, ...outerDown]) {
    svg += drawNode(n);
  }

  // "+N more" pills per ring per direction when overflow
  const overflowPill = (visible, total, ringR, direction) => {
    if (total <= visible.length) return '';
    const angle = direction === 'up' ? -Math.PI / 2 - 1.45 : Math.PI / 2 + 1.45; // beyond the arc end
    const px = cx + Math.cos(angle) * ringR;
    const py = cy + Math.sin(angle) * ringR;
    let p = '<g>';
    p += '<rect x="' + (px - 22) + '" y="' + (py - 9) + '" width="44" height="18" rx="9"' +
         ' fill="#fff" stroke="rgba(0,0,0,0.12)" stroke-dasharray="3 3"/>';
    p += '<text x="' + px + '" y="' + (py + 4) + '" text-anchor="middle" font-size="9" fill="#6E6E73">+' + (total - visible.length) + ' more</text>';
    p += '</g>';
    return p;
  };
  svg += overflowPill(innerUp, tier1Parents.length, innerR, 'up');
  svg += overflowPill(innerDown, tier1Children.length, innerR, 'down');
  svg += overflowPill(outerUp, tier2Up.length, outerR, 'up');
  svg += overflowPill(outerDown, tier2Down.length, outerR, 'down');

  // Generation labels (subtle, top-left)
  svg += '<text x="14" y="18" font-size="9" fill="#86868B" letter-spacing="1" font-weight="600">LINEAGE</text>';
  svg += '<text x="14" y="' + (H - 14) + '" font-size="9" fill="#86868B" font-style="italic">parents · grandparents above · children · grandchildren below</text>';

  svg += '</svg>';
  return svg;
}

// ── Main renderer ──────────────────────────────────────────────────
// `opts` (all optional, default off) lets the dual-strain wrapper turn
// off heavyweight or footer-only sections so each column stays compact
// and the popup-level footer renders just once for a cross.
//   compactLineage   — replace the orbital lineage with a chip list
//   hideOrbital      — drop the lineage section entirely
//   showFaq          — render the FAQ block (DEFAULT off; in-chat chatbot popup
//                      hides faqs, standalone menu popup passes showFaq:true)
//   hideFAQ          — force-drop the FAQ block (applied on top of showFaq;
//                      used for dual-strain column suppression)
//   hideDisclaimers  — drop the disclaimer block
//   hideFeedback     — drop the helpful/not-helpful row
function _renderInfographic(s, opts) {
  opts = opts || {};
  const hasTypeDissent = s.typeConsensus.dissentVotes > 0;
  const validTerps = s.terpenes.filter(t => t.pct != null);
  const totalPct = validTerps.reduce((a, b) => a + b.pct, 0) || 1;

  let html = '';

  // Title
  //
  // The small "Strain information" caption above the h1 makes it explicit
  // that this overlay is showing strain-level data (community + catalog),
  // not specific information about the dispensary product the user clicked.
  // Customers were confused by seeing the full product_name (e.g.
  // "SOUR DIESEL X DURBAN POISON | 0.5G | 2PK PRE-ROLLS") here and assumed
  // the alias / effect data was for THAT specific product.
  html += '<div class="info2-title">';
  html += '  <div class="info2-title-caption">Strain information</div>';
  html += '  <h2 class="info2-name">' + _escape(s.name) + '</h2>';
  if (s.aliases.length) {
    html += '  <div class="info2-aliases">' + _escape(s.aliases.join(' · ')) + '</div>';
  }
  html += '  <span class="info2-type-pill info2-type-pill--' + _typeClass(s.typeConsensus.label) + '">' + _escape(s.typeConsensus.label) + '</span>';
  // "Store says" caption — sits centered DIRECTLY below the type pill
  // when the store reports its own strain_type for this product. Was
  // previously emitted as a separate block under the consensus meter,
  // which read as a footnote rather than an attribution tied to the
  // pill. Moving it inside .info2-title (a text-align:center container)
  // gives it the centered placement V3 calls for. Per-strain field, so
  // this renders correctly inside each tab in dual-strain mode too.
  if (s.storeOverlay && s.storeOverlay.strain_type) {
    html += '  <div class="info2-type-store-says">';
    html += '    <span class="info2-type-store-says-lbl">Store says</span>';
    html += '    <span class="info2-type-store-says-val">' + _escape(s.storeOverlay.strain_type) + '</span>';
    html += '  </div>';
  }
  html += '</div>';

  // Type consensus split meter — only renders when there's actual dissent
  // across the universal sources. Most strains hit unanimous consensus, in
  // which case we skip the bar.
  if (hasTypeDissent) {
    const tc = s.typeConsensus;
    html += '<div class="info2-type-consensus">';
    html += '  <div class="info2-type-meter">';
    html += '    <span class="info2-type-fill" style="flex:' + tc.votes + '" data-type="' + _typeClass(tc.label) + '"></span>';
    html += '    <span class="info2-type-fill" style="flex:' + tc.dissentVotes + '" data-type="' + _typeClass(tc.dissent) + '"></span>';
    html += '  </div>';
    html += '  <div class="info2-type-legend">';
    html += '    <div class="info2-type-legend-item"><span class="info2-type-sw info2-type-sw--' + _typeClass(tc.label) + '"></span><span class="info2-type-legend-num">' + tc.votes + '</span><span class="info2-type-legend-lbl">' + _escape(tc.label) + '</span></div>';
    html += '    <div class="info2-type-legend-sep">vs</div>';
    html += '    <div class="info2-type-legend-item alt"><span class="info2-type-sw info2-type-sw--' + _typeClass(tc.dissent) + '"></span><span class="info2-type-legend-num">' + tc.dissentVotes + '</span><span class="info2-type-legend-lbl">' + _escape(tc.dissent) + '</span></div>';
    html += '    <div class="info2-type-legend-total">of ' + tc.of + ' sources</div>';
    html += '  </div>';
    html += '</div>';
  }

  // (Store-says caption was relocated May 2026 — now emitted inside
  // .info2-title directly below the type pill, regardless of dissent
  // state, so it reads as an attribution paired with the pill rather
  // than a footnote under the consensus meter.)

  // Stats row — THC was removed (stores already hide it from sources).
  // Now: highest reported effect on the left (gets more space) + 5-dot
  // popularity badge on the right. Popularity formula = vote-weighted
  // log10 of community engagement + source-coverage breadth.
  const topEff = s.effects[0];
  html += '<div class="info2-stats-row">';
  if (topEff) {
    html += '  <div class="info2-top-effect">';
    html += '    <div class="info2-top-effect-label">Highest reported effect</div>';
    html += '    <div class="info2-top-effect-main"><span class="info2-top-effect-name">' + _escape(topEff.name) + '</span></div>';
    html += '    <div class="info2-top-effect-stats">';
    if (topEff.votes) {
      html += '      <div><strong>' + topEff.votes.toLocaleString() + '</strong> votes</div>';
    }
    html += '      <div>' + topEff.agreement + (topEff.agreement === 1 ? ' source confirms' : ' sources confirm') + '</div>';
    html += '    </div>';
    html += '  </div>';
  } else {
    html += '  <div class="info2-top-effect"><div class="info2-top-effect-label">No effect data</div></div>';
  }
  html += '  <div class="info2-stats-divider"></div>';
  html += '  <div class="info2-pop-block">';
  html += '    <div class="info2-pop-label">Popularity</div>';
  // Format review counts with K-suffix once they hit 1000 to keep the meta
  // line compact (Apple-strict tiny text). Below 1000 show the exact number.
  const _fmtPopCount = (n) => n >= 1000
    ? (n / 1000).toFixed(n >= 10000 ? 0 : 1) + 'K'
    : n.toLocaleString();
  if (s.popRating) {
    // #440 — numeric rating replaces the legacy 5-dot meter: "★ 4.5" with
    // the tier word kept beneath and "from 2.0K reviews" as the meta line.
    // The count prefers the SAME merged review_count the legacy meta line
    // showed (popVotes when the signal is 'reviews') so the number never
    // shifts between designs; falls back to the rating's own weighted
    // denominator when the merged count is unavailable.
    const _rv = s.popRating.value.toFixed(1);
    const _rc = (s.popSignalSource === 'reviews' && s.popVotes > 0)
      ? s.popVotes
      : s.popRating.count;
    html += '    <div class="info2-pop-rating" role="img" aria-label="Rated ' + _rv + ' out of 5"><span class="info2-pop-star">★</span>' + _rv + '</div>';
    html += '    <div class="info2-pop-tier">' + _escape(s.popTier) + '</div>';
    if (_rc > 0) {
      html += '    <div class="info2-pop-meta">from ' + _fmtPopCount(_rc) + ' reviews</div>';
    }
  } else {
    // No rating data anywhere — keep the legacy display untouched:
    // 5-dot meter + tier word + raw count. Never show "0.0".
    html += '    <div class="info2-pop-dots" aria-label="Popularity ' + s.popDots + ' of 5">';
    for (let i = 0; i < 5; i++) {
      html += '<span' + (i < s.popDots ? ' class="on"' : '') + '></span>';
    }
    html += '    </div>';
    html += '    <div class="info2-pop-tier">' + _escape(s.popTier) + '</div>';
    if (s.popVotes > 0) {
      // The label switches to "reviews" when popSignalSource is 'reviews' (the
      // top-level merged review_count) vs "votes" when falling back to the
      // legacy effect-vote sum.
      const _popLbl = s.popSignalSource === 'reviews' ? 'reviews' : 'votes';
      html += '    <div class="info2-pop-meta">' + _fmtPopCount(s.popVotes) + ' ' + _popLbl + '</div>';
    }
  }
  html += '  </div>';
  html += '</div>';

  // Viz: radar + terpene bar + terpene cards
  // Note: title is just "Effects" now — the standalone effects chip-list
  // section was removed (per user) so the radar is the single home for
  // effect data. Aroma still shows in its own "Aroma notes" section below.
  html += '<div class="info2-viz">';
  html += '  <div class="info2-viz-label">Effects</div>';
  // Pass the store-confirmed effects list (if any) so the radar renderer
  // can mark each matching label with a small green dot.
  const _storeEffectsForRadar = (s.storeOverlay && Array.isArray(s.storeOverlay.effects))
    ? s.storeOverlay.effects : null;
  html += '  <div class="info2-radar-wrap">' + _renderRadar(s.effects.slice(0, 6), _storeEffectsForRadar, opts && opts.stackRadarLabels) + '</div>';
  // Legend + extras-line: only render when we actually have store overlay
  // data, otherwise the popup looks identical to the pre-overlay design.
  if (s.storeOverlay && Array.isArray(s.storeOverlay.effects) && s.storeOverlay.effects.length) {
    const _radarNames = new Set(s.effects.slice(0, 6).map(e => String(e.name || '').trim().toLowerCase()));
    const _storeOnly = [];
    for (const eff of s.storeOverlay.effects) {
      if (typeof eff !== 'string') continue;
      const k = eff.trim();
      if (!k) continue;
      if (!_radarNames.has(k.toLowerCase())) _storeOnly.push(k);
    }
    // The legend ALWAYS renders when overlay has any effects — explains the dot.
    html += '  <div class="info2-effects-legend">Green dot &mdash; store confirms this effect</div>';
    if (_storeOnly.length) {
      const _list = _storeOnly.map(n => '<strong>' + _escape(n) + '</strong>').join(', ');
      html += '  <div class="info2-effects-extras">Store also says this product has ' + _list + '</div>';
    }
  }
  // Build the dominance bar by merging universal-source values + store
  // batch values. Each segment's flex weight is its percent of the
  // combined total, so the bar fills proportionally regardless of how
  // many sources reported a number. Store-only segments (terpenes the
  // store reports that universal doesn't have a numeric value for) get
  // a diagonal-stripe texture via .store-only-extra so the customer can
  // distinguish "consensus" from "your batch only" at a glance.
  function _normTerpKeyEarly(name) {
    return String(name || '').toLowerCase()
      .replace(/^(alpha|beta|α|β)[-\s]*/, '')
      .replace(/[\s-]/g, '')
      .trim();
  }
  function _terpFamilyFromName(name) {
    const k = _normTerpKeyEarly(name);
    const known = ['caryophyllene','myrcene','limonene','pinene','linalool',
                   'humulene','terpinolene','ocimene'];
    for (const fam of known) if (k.includes(fam)) return fam;
    return 'default';
  }
  // Walk universal terps first, accumulating segments. Track which store
  // entries get covered so leftover ones become store-only segments.
  const _storeMap = {};
  if (s.storeOverlay && s.storeOverlay.terpenes && typeof s.storeOverlay.terpenes === 'object') {
    for (const [k, v] of Object.entries(s.storeOverlay.terpenes)) {
      const num = Number(v);
      if (!Number.isFinite(num) || num <= 0) continue;
      _storeMap[_normTerpKeyEarly(k)] = { name: k, pct: num };
    }
  }
  const _segments = [];
  const _seen = new Set();
  for (const t of s.terpenes) {
    const _k = _normTerpKeyEarly(t.name);
    _seen.add(_k);
    // Prefer the universal pct when it's a real number; otherwise fall
    // back to the store pct so the segment still shows up visually.
    const _univ = (typeof t.pct === 'number' && t.pct > 0) ? t.pct : null;
    const _store = _storeMap[_k] ? _storeMap[_k].pct : null;
    const _pct = _univ != null ? _univ : _store;
    if (_pct == null) continue;
    const _isStoreOnly = _univ == null && _store != null;
    _segments.push({
      name: t.name, family: t.family || _terpFamilyFromName(t.name),
      pct: _pct, storeOnly: _isStoreOnly,
      title: t.name + ' ' + _pct.toFixed(2) + '%'
        + (_isStoreOnly ? ' (your batch)' : '')
    });
  }
  // Append any store-only terpenes that didn't match a universal row.
  for (const [k, info] of Object.entries(_storeMap)) {
    if (_seen.has(k)) continue;
    _segments.push({
      name: info.name, family: _terpFamilyFromName(info.name),
      pct: info.pct, storeOnly: true,
      title: info.name + ' ' + info.pct.toFixed(2) + '% (your batch)'
    });
  }
  // Compute combined total just for this bar so flex weights make sense
  // even when validTerps is empty (universal had nothing numeric).
  const _barTotal = _segments.reduce((a, b) => a + b.pct, 0) || 1;
  if (_segments.length) {
    html += '  <div class="info2-terp-bar">';
    for (const seg of _segments) {
      const _cls = seg.storeOnly ? ' store-only-extra' : '';
      html += '    <span class="info2-terp-seg' + _cls + '" data-family="' + seg.family + '" style="flex:' + (seg.pct / _barTotal) + '" title="' + _escape(seg.title) + '"></span>';
    }
    html += '  </div>';
  }
  // ── Terpene cards ──────────────────────────────────────────────────
  // Single unified render — every terpene gets ONE rich card. The store
  // batch info is now an inline indicator on the card (no separate
  // comparison block above the cards).
  //
  // Order (user spec 2026-05-11):
  //   1. Cards that have store-batch data come first, sorted DESC by the
  //      store-batch percent (the dominant batch terpene leads).
  //   2. Cards without store-batch data come after, in their original
  //      catalog order. Each gets a small muted note "Not in your batch ·
  //      sources say it may be present".
  //
  // s.terpeneComparison (built by backend _diff_terpenes) is the canonical
  // store-batch source. We fall back to s.storeOverlay.terpenes when the
  // diff isn't provided (older deploys). Both feed into _storeTerpMap.
  function _normTerpKey(name) {
    return String(name || '').toLowerCase()
      .replace(/^(alpha|beta|α|β)[-\s]*/, '')
      .replace(/[\s-]/g, '')
      .trim();
  }
  const _storeTerpMap = {};
  // Pull store-batch percents from s.terpeneComparison first (richer),
  // then fill any gaps from s.storeOverlay.terpenes.
  if (Array.isArray(s.terpeneComparison)) {
    for (const row of s.terpeneComparison) {
      if (!row || !row.name) continue;
      const num = Number(row.store_percent);
      if (!Number.isFinite(num) || num <= 0) continue;
      _storeTerpMap[_normTerpKey(row.name)] = num;
    }
  }
  if (s.storeOverlay && s.storeOverlay.terpenes && typeof s.storeOverlay.terpenes === 'object') {
    for (const [k, v] of Object.entries(s.storeOverlay.terpenes)) {
      const num = Number(v);
      if (!Number.isFinite(num) || num <= 0) continue;
      const key = _normTerpKey(k);
      if (_storeTerpMap[key] == null) _storeTerpMap[key] = num;
    }
  }
  // Build the unified card list. Each entry: { kind, name, family, taste,
  // effect, pct, pctMax, sources, of, storePct }. `kind` is either
  // 'catalog' (came from s.terpenes — has full universal context) or
  // 'storeOnly' (in store batch but not in any universal source — we
  // synthesize taste/effect from _TERPENE_DEFAULTS).
  const _storeUsed = new Set();
  const _cardList = [];
  for (const t of s.terpenes) {
    const k = _normTerpKey(t.name);
    const storePct = _storeTerpMap[k];
    if (storePct != null) _storeUsed.add(k);
    _cardList.push({
      kind: 'catalog',
      name: t.name,
      family: t.family,
      taste: t.taste,
      effect: t.effect,
      pct: t.pct,
      pctMax: t.pctMax,
      sources: t.sources,
      of: t.of,
      storePct: storePct != null ? storePct : null,
    });
  }
  // Store-only terpenes (in batch but no universal source) — render as
  // synthesized cards so the customer still sees their batch info.
  if (s.storeOverlay && s.storeOverlay.terpenes) {
    for (const [rawName, rawPct] of Object.entries(s.storeOverlay.terpenes)) {
      const num = Number(rawPct);
      if (!Number.isFinite(num) || num <= 0) continue;
      const k = _normTerpKey(rawName);
      if (_storeUsed.has(k)) continue;
      _storeUsed.add(k);
      const _pretty = String(rawName)
        .replace(/^(Alpha|Beta)/, '$1-')
        .replace(/^(alpha|beta)/, m => m.charAt(0).toUpperCase() + m.slice(1) + '-');
      const fam = _terpFamily(rawName);
      const def = _TERPENE_DEFAULTS[fam] || _TERPENE_DEFAULTS.default;
      _cardList.push({
        kind: 'storeOnly',
        name: _pretty,
        family: fam,
        taste: def.taste,
        effect: def.effect,
        pct: null,
        pctMax: null,
        sources: 0,
        of: 0,
        storePct: num,
      });
    }
  }
  // Sort: store-batch cards first (DESC by storePct), then the rest in
  // their original order. Stable sort preserves catalog order for the
  // "not in batch" tail.
  _cardList.sort((a, b) => {
    const aHas = a.storePct != null;
    const bHas = b.storePct != null;
    if (aHas && !bHas) return -1;
    if (!aHas && bHas) return 1;
    if (aHas && bHas) return b.storePct - a.storePct;
    return 0;
  });

  if (_cardList.length) {
    html += '  <ul class="info2-terp-cards">';
    for (const c of _cardList) {
      const _cardCls = c.kind === 'storeOnly'
        ? 'info2-terp-card store-only-extra'
        : 'info2-terp-card';
      html += '    <li class="' + _cardCls + '">';
      html += '      <div class="info2-terp-card-hdr">';
      html += '        <span class="info2-terp-sw" data-family="' + c.family + '"></span>';
      html += '        <span class="info2-terp-name">' + _escape(c.name) + '</span>';
      // Universal % when known, else "trace" (catalog cards only) or the
      // store-only badge (storeOnly cards have no universal %, the store
      // batch line below carries the number).
      html += '        <span class="info2-terp-pct">';
      if (c.pct != null) {
        html += c.pctMax != null
          ? (c.pct.toFixed(2) + '–' + c.pctMax.toFixed(2) + '%')
          : (c.pct.toFixed(2) + '%');
      } else if (c.kind === 'storeOnly') {
        html += '<span class="store-pct">' + c.storePct.toFixed(2) + '% <em>your batch</em></span>';
      } else {
        html += '<em>trace</em>';
      }
      html += '</span>';
      // Store batch chip RIGHT OF the percent range, on the same header line.
      // User explicit: 'right-hand side of that it should show the batch percentage'.
      if (c.kind === 'catalog' && c.storePct != null) {
        html += '        <span class="info2-terp-batch-chip">Store batch: ' +
                c.storePct.toFixed(2) + '%</span>';
      }
      html += '      </div>';
      html += '      <div class="info2-terp-meta">';
      html += '        <div><em>Taste:</em> ' + _escape(c.taste) + '</div>';
      html += '        <div><em>Effect:</em> ' + _escape(c.effect) + '</div>';
      if (c.kind === 'catalog') {
        // Show ONLY how many sources report this terpene — no "/of"
        // denominator and no gray remainder bars (user request 2026-05-20).
        html += '        <div class="info2-terp-sources"><span class="info2-terp-src-bars">';
        for (let i = 0; i < c.sources; i++) {
          html += '<span class="on"></span>';
        }
        html += '        </span><span>' + c.sources + (c.sources === 1 ? ' source reports' : ' sources report') + '</span></div>';
      } else {
        html += '        <div class="info2-terp-sources"><span>store batch only &middot; no universal sources</span></div>';
      }
      html += '      </div>';
      // Tail note for catalog cards that DON'T have store-batch data —
      // makes the gap explicit to the customer without adding a whole
      // separate row above the cards.
      if (c.kind === 'catalog' && c.storePct == null) {
        html += '      <div class="info2-terp-card-batch--missing">' +
                'Might not be in your batch &middot; but other sources confirm the presence of this strain</div>';
      }
      html += '    </li>';
    }
    html += '  </ul>';
  } else {
    html += '  <div class="info2-terp-empty">Terpene data not reported by sources</div>';
  }
  html += '</div>';

  // Arbitration confidence chip — rendered above the description when
  // the backend returned a polished answer. Plain text chip, no decorative
  // color (respects Apple UI rules). "Low" shows a warning instead.
  if (s.arbitrationLabel) {
    const chipClass = s.arbitrationLabel === 'Low'
      ? 'info2-arb-chip info2-arb-chip-low'
      : 'info2-arb-chip';
    const chipText = s.arbitrationLabel === 'Low'
      ? 'Cross-checked · low confidence'
      : (s.usingPolishedDescription
          ? 'Cross-checked · ' + s.arbitrationLabel.toLowerCase() + ' confidence'
          : '');
    if (chipText) {
      html += '<div class="' + chipClass + '">' + chipText + '</div>';
    }
  }

  // Description — 3-line preview with "Read more" toggle for long copy.
  // When multiple sources wrote different blurbs we ALSO show a separate
  // "Sources differ — see each take" collapsible below.
  if (s.description) {
    const descText = _escape(_stripHtml(s.description));
    const isLong = s.description.length > 260;  // rough cut; clamp handles visual
    html += '<div class="info2-desc-wrap" data-more="' + (isLong ? '1' : '0') + '">';
    if (isLong) {
      html += '  <div class="info2-desc-collapsed">' + descText + '</div>';
      html += '  <div class="info2-desc-full">' + descText + '</div>';
      html += '  <button class="info2-desc-toggle" type="button" aria-expanded="false">' +
              '<span class="info2-desc-toggle-label">Read more</span>' +
              '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 9l6 6 6-6"/></svg>' +
              '</button>';
    } else {
      html += '  <p class="info2-desc">' + descText + '</p>';
    }
    html += '</div>';
  }
  // "See more descriptions (N)" block removed per user feedback —
  // arbitrated description above is the single source of truth shown
  // to customers; per-source variance is admin-facing only.

  // Flavor cloud — pills carry data-flavor-name + data-flavor-kind so the
  // delegated hover handler (bound on the widget body) can pop a per-source
  // attribution tooltip. Aromas reuse the same handler under data-flavor-kind="aroma".
  // When storeOverlay.flavors is present, matched chips get .store-tagged
  // (coral underline + ✓) and store-only flavors render as additional
  // dashed-orange .store-only-extra chips at the end of the cloud.
  if (s.flavors.length || (s.storeOverlay && Array.isArray(s.storeOverlay.flavors) && s.storeOverlay.flavors.length)) {
    const _storeFlavorSet = new Set();
    if (s.storeOverlay && Array.isArray(s.storeOverlay.flavors)) {
      for (const x of s.storeOverlay.flavors) {
        if (typeof x === 'string' && x.trim()) _storeFlavorSet.add(x.trim().toLowerCase());
      }
    }
    const _univSet = new Set();
    for (const f of s.flavors) _univSet.add(String(f.name || '').trim().toLowerCase());

    html += '<section class="info2-section"><h3>Flavor notes</h3>';
    html += '  <div class="info2-flavor-cloud">';
    for (const f of s.flavors) {
      const fs = 13 + (f.agreement / Math.max(f.of, 1)) * 13;
      const op = 0.5 + (f.agreement / Math.max(f.of, 1)) * 0.5;
      const _isStore = _storeFlavorSet.has(String(f.name || '').trim().toLowerCase());
      const _cls = 'info2-flavor' + (_isStore ? ' store-tagged' : '');
      html += '    <span class="' + _cls + '" data-flavor-name="' + _escape(f.name) +
              '" data-flavor-kind="flavor" style="font-size:' + fs + 'px;opacity:' + op + '">' +
              _escape(f.name) + '</span>';
    }
    // Append store-only flavors as their own dashed-orange chips.
    if (s.storeOverlay && Array.isArray(s.storeOverlay.flavors)) {
      for (const fname of s.storeOverlay.flavors) {
        if (typeof fname !== 'string') continue;
        const k = fname.trim();
        if (!k) continue;
        if (_univSet.has(k.toLowerCase())) continue;
        html += '    <span class="info2-flavor store-only-extra" data-flavor-name="' + _escape(k) +
                '" data-flavor-kind="flavor" style="font-size:16px;opacity:1">' +
                _escape(k) + '</span>';
      }
    }
    html += '  </div>';
    html += '</section>';
  }
  // Aroma cloud — only renders if the API ever surfaces mega_aromas_agg.
  // Same shape + tooltip wiring as flavors.
  if (s.aromas && s.aromas.length) {
    html += '<section class="info2-section"><h3>Aroma notes</h3>';
    html += '  <div class="info2-flavor-cloud">';
    for (const a of s.aromas) {
      const fs = 13 + (a.agreement / Math.max(a.of, 1)) * 13;
      const op = 0.5 + (a.agreement / Math.max(a.of, 1)) * 0.5;
      html += '    <span class="info2-flavor" data-flavor-name="' + _escape(a.name) +
              '" data-flavor-kind="aroma" style="font-size:' + fs + 'px;opacity:' + op + '">' +
              _escape(a.name) + '</span>';
    }
    html += '  </div>';
    html += '</section>';
  }

  // Split: may help / watch for
  if (s.mayHelp.length || s.sideEffects.length) {
    html += '<div class="info2-split">';
    if (s.mayHelp.length) {
      html += '  <section class="info2-section"><h3>May help</h3>' + _renderRichStack(s.mayHelp, 'sky') + '</section>';
    }
    if (s.sideEffects.length) {
      html += '  <section class="info2-section"><h3>Watch for</h3>' + _renderRichStack(s.sideEffects, 'rose') + '</section>';
    }
    html += '</div>';
  }

  // Lineage — filterable orbital, matches the reference design exactly:
  // 5 concentric rings, full 360° node distribution per ring, HTML pill
  // nodes positioned absolutely (not SVG), filter chips above to toggle
  // generations on/off, hover tooltip on each node.
  //
  // Dual-strain mode: when opts.compactLineage is true, render a small
  // chip list instead of the orbital (orbital uses aspect-ratio 1/1 and
  // takes the full popup width — half that is too cramped for nodes).
  // When opts.hideOrbital is true, skip lineage entirely.
  if (!opts.hideOrbital && (s.parents.length || s.children.length)) {
    if (opts.compactLineage) {
      html += '<section class="info2-section">';
      html += '  <h3>Lineage</h3>';
      html += _renderMiniLineage(s.parents, s.children);
      html += '</section>';
    } else {
      // Center label: prefer the resolved canonical strain name (e.g.
      // "Garlic" instead of the raw POS product name "Satori | Garlic
      // Drip | Pre-Roll 1g"). Falls back through arbitrated → first
      // alias → product name.
      const orbitName = s.arbitratedCanonicalName
        || (s.aliases && s.aliases[0])
        || s.name;
      html += '<section class="info2-section">';
      html += '  <h3>Lineage</h3>';
      html += _renderOrbital(orbitName, s.parents, s.children, s.familyTree,
                              s.typeConsensus.label, s.thc);
      html += '</section>';
    }
  }

  // FAQ
  // §3: gated on opts.showFaq (DEFAULT false). The in-chat chatbot popup omits
  // opts.showFaq → faqs HIDDEN; the standalone menu strain-reviews popup passes
  // showFaq:true → faqs SHOWN. opts.hideFAQ is still honored (dual-strain
  // column suppression) on top of the showFaq gate.
  if (opts.showFaq && !opts.hideFAQ && s.faqs && s.faqs.length) {
    html += '<section class="info2-section"><h3>FAQ</h3>';
    html += '  <div class="info2-faq">';
    for (const f of s.faqs.slice(0, 6)) {
      const q = (f.q || '').trim();
      const a = (f.a || '').trim();
      if (!q || !a) continue;
      html += '    <details class="info2-faq-item">';
      html += '      <summary><span>' + _escape(q) + '</span><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 9l6 6 6-6"/></svg></summary>';
      html += '      <p>' + _escape(_stripHtml(a)) + '</p>';
      html += '    </details>';
    }
    html += '  </div>';
    html += '</section>';
  }

  // Disclaimers
  if (!opts.hideDisclaimers) {
    html += '<div class="info2-disclaimers">';
    html += '  <p class="info2-dis-medical"><strong>Not medical advice.</strong> Aggregated from public references and community reports; consult a healthcare provider before use.</p>';
    html += '  <p class="info2-dis-prop">Proprietary VirtualBudz data · cross-checked against independent cannabis references.</p>';
    html += '</div>';
  }

  // Helpful / Not helpful feedback. Apple-strict: monochrome SVG icons
  // (currentColor only — no decorative fill), no emojis, plain text label.
  // Click handler is wired in openStrainOverlay (delegated, fires once
  // per overlay instance).
  if (!opts.hideFeedback) {
    html += '<div class="info2-feedback" data-voted="0">';
    html += '  <div class="info2-feedback-label">Was this helpful?</div>';
    html += '  <div class="info2-feedback-buttons">';
    html += '    <button type="button" class="info2-fb-btn" data-fb="up" aria-label="Helpful">';
    html += '      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">';
    html += '        <path d="M7 11v9H4a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1h3z"/>';
    html += '        <path d="M7 11l4-7a2 2 0 0 1 3.6 1.6L13 11h5.5a2 2 0 0 1 2 2.3l-1.2 6a2 2 0 0 1-2 1.7H7"/>';
    html += '      </svg>';
    html += '    </button>';
    html += '    <button type="button" class="info2-fb-btn" data-fb="down" aria-label="Not helpful">';
    html += '      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">';
    html += '        <path d="M17 13V4h3a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1h-3z"/>';
    html += '        <path d="M17 13l-4 7a2 2 0 0 1-3.6-1.6L11 13H5.5a2 2 0 0 1-2-2.3l1.2-6a2 2 0 0 1 2-1.7H17"/>';
    html += '      </svg>';
    html += '    </button>';
    html += '  </div>';
    html += '  <div class="info2-feedback-thanks" data-state="hidden">Thanks for the feedback.</div>';
    html += '</div>';
  }

  return html;
}

// ── Compact lineage chip list (used in dual-strain columns) ─────────
// Replaces the orbital lineage SVG when each strain is shown at half
// width. Plain monochrome chips, no orbital recentering, no clicks
// wired — informational only. Apple-strict: no color, no icons.
function _renderMiniLineage(parents, children) {
  const cleanList = (list, getName) => {
    const out = [];
    const seen = new Set();
    for (const it of (list || [])) {
      const name = getName(it);
      if (!name || _isPolluted(name)) continue;
      const k = name.trim().toLowerCase();
      if (!k || seen.has(k)) continue;
      seen.add(k);
      out.push(name.trim());
    }
    return out;
  };
  const ps = cleanList(parents, p => (p && (p.name || p)) || '');
  const cs = cleanList(children, c => (typeof c === 'string' ? c : (c && c.name) || ''));

  let h = '<div class="info2-mini-lineage">';
  h += '  <div class="info2-mini-lineage-row">';
  h += '    <div class="info2-mini-lineage-label">Parents</div>';
  if (ps.length) {
    h += '    <div class="info2-mini-lineage-chips">';
    for (const n of ps.slice(0, 8)) {
      h += '<span class="info2-mini-lineage-chip">' + _escape(n) + '</span>';
    }
    h += '    </div>';
  } else {
    h += '    <div class="info2-mini-lineage-empty">Not reported</div>';
  }
  h += '  </div>';
  if (cs.length) {
    h += '  <div class="info2-mini-lineage-row">';
    h += '    <div class="info2-mini-lineage-label">Children</div>';
    h += '    <div class="info2-mini-lineage-chips">';
    for (const n of cs.slice(0, 8)) {
      h += '<span class="info2-mini-lineage-chip">' + _escape(n) + '</span>';
    }
    h += '    </div>';
    if (cs.length > 8) {
      h += '    <div class="info2-mini-lineage-empty">+ ' + (cs.length - 8) + ' more</div>';
    }
    h += '  </div>';
  }
  h += '</div>';
  return h;
}

// ── Dual-strain renderer (tab-strip pattern, May 2026 v3) ───────────
// Called when the API returns { primary, secondary } — typically when
// the product is a documented cross like "Baked Alaska × Georgia
// Pie". Replaces the older side-by-side dual-grid layout. We render
// ONE strain at a time with a segmented tab strip at top and a muted
// caption naming the cross. Switching tabs is a pure-DOM swap (both
// strain blobs already in memory from the API response) — no fetch.
//
// `activeKey` is "primary" | "secondary" — controls which tab is on.
// `innerOpts` flows straight to _renderInfographic so callers can
// pick a compact (hideOrbital:true) or full body.
//
// Backwards compatible: openStrainOverlay still calls
// _renderInfographic(s) directly for legacy single-strain shapes.
function _renderTabbedInfographic(primary, secondary, activeKey, innerOpts) {
  // primary/secondary already passed through _adaptStrainData, so .name is
  // the resolved clean strain name (prefers canonical_name internally).
  const pName = (primary && primary.name) || 'Strain A';
  const sName = (secondary && secondary.name) || 'Strain B';
  const activeIsP = activeKey !== 'secondary';
  const active = activeIsP ? primary : secondary;
  const opts = innerOpts || {};

  let html = '';

  html += '<div class="info2-strain-tabs-wrap">';
  html += '  <div class="info2-strain-tabs" role="tablist" aria-label="Strain selector">';
  html += '    <button class="info2-strain-tab' + (activeIsP ? ' active' : '') + '"' +
          '            type="button" data-strain-key="primary" role="tab"' +
          '            aria-selected="' + (activeIsP ? 'true' : 'false') + '">' +
          _escape(pName) + '</button>';
  html += '    <button class="info2-strain-tab' + (!activeIsP ? ' active' : '') + '"' +
          '            type="button" data-strain-key="secondary" role="tab"' +
          '            aria-selected="' + (!activeIsP ? 'true' : 'false') + '">' +
          _escape(sName) + '</button>';
  html += '  </div>';
  html += '</div>';

  // Caption — italic muted, names the cross. Use existing
  // .info2-aliases visual style via shared .info2-strain-tabs-caption.
  // The active strain name is bolded inside the caption to reinforce
  // which side is currently visible.
  html += '<div class="info2-strain-tabs-caption">';
  html += 'Cross of <strong>' + _escape(pName) + '</strong> ' +
          '<span style="color:#86868B;font-weight:400;">×</span> ' +
          '<strong>' + _escape(sName) + '</strong>';
  html += ' — viewing one at a time';
  html += '</div>';

  // Active strain body. Wrapped so tab clicks can target a single
  // node for innerHTML swap without disturbing the tab strip above.
  html += '<div class="info2-strain-tab-body" data-strain-key="' +
          (activeIsP ? 'primary' : 'secondary') + '">';
  html += _renderInfographic(active, opts);
  html += '</div>';

  return html;
}

function _renderRichStack(items, tone) {
  const maxVotes = Math.max(...items.map(i => i.votes || 0), 1);
  let html = '<ul class="info2-rich-stack" data-tone="' + tone + '">';
  for (const it of items) {
    html += '  <li>';
    html += '    <div class="info2-rich-top"><span class="info2-rich-name">' + _escape(it.name) + '</span>';
    if (it.votes) {
      html += '<span class="info2-rich-votes">' + it.votes.toLocaleString() + '</span>';
    }
    html += '    </div>';
    // Bar — prefer votes-based fill, else agreement-based
    const fillPct = it.votes ? (it.votes / maxVotes) * 100 : (it.agreement / Math.max(it.of, 1)) * 100;
    html += '    <div class="info2-rich-bar"><span style="width:' + fillPct + '%"></span></div>';
    // Show ONLY how many sources report this effect/flavor — no "/of"
    // denominator and no gray remainder bars (user request 2026-05-20).
    html += '    <div class="info2-rich-sources"><span class="info2-rich-src-bars">';
    for (let i = 0; i < it.agreement; i++) {
      html += '<span class="on"></span>';
    }
    html += '    </span><span>' + it.agreement + (it.agreement === 1 ? ' source' : ' sources') + '</span></div>';
    html += '  </li>';
  }
  html += '</ul>';
  return html;
}

async function _fetchStrainData(widget, productId) {
  const apiBase = widget.API_BASE || (window.CHATBOT_CONFIG && window.CHATBOT_CONFIG.API_URL) || window.location.origin;
  const businessId = widget.businessId || window.CHATBOT_BUSINESS_ID || '';
  try {
    const res = await fetch(
      apiBase + '/admin/public/strain-info/' + encodeURIComponent(businessId) + '?product_id=' + encodeURIComponent(productId)
    );
    const j = await res.json();
    return (j && j.data) || null;
  } catch (e) {
    console.warn('[strain-info] fetch failed', e);
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────
// Engagement tracking — fire-and-forget POSTs to record customer-facing
// strain widget events (inline open, full open, thumbs up/down). Must
// never throw / await long enough to disrupt UX. Backend silently ignores
// unknown actions and never errors back to the client.
// ─────────────────────────────────────────────────────────────────────
function _vbStrainSessionId() {
  // Reuse the widget's own guest id (set by widget.js at startup) when
  // available — same value the chat uses, so events tie back to the
  // same customer session. Fall back to a per-page anonymous id stored
  // in window so it persists across renders within one page-load.
  try {
    const guest = (typeof sessionStorage !== 'undefined')
      ? sessionStorage.getItem('premium_guest_id')
      : null;
    if (guest) return String(guest).slice(0, 64);
  } catch (e) { /* private mode / disabled storage — fall through */ }

  if (typeof window !== 'undefined') {
    if (!window.__vbSessionId) {
      window.__vbSessionId =
        'vb_' + Math.random().toString(36).slice(2, 10) +
        Date.now().toString(36).slice(-6);
    }
    return String(window.__vbSessionId).slice(0, 64);
  }
  return null;
}

function _vbApiBase(widget) {
  return (widget && widget.API_BASE)
    || (typeof window !== 'undefined' && window.CHATBOT_CONFIG && window.CHATBOT_CONFIG.API_URL)
    || (typeof window !== 'undefined' ? window.location.origin : '');
}

function _vbBusinessId(widget) {
  return (widget && widget.businessId)
    || (typeof window !== 'undefined' && window.CHATBOT_BUSINESS_ID)
    || '';
}

async function recordStrainEvent(widget, action, productId) {
  // Validation lives server-side; we don't gate on action here so future
  // additions Just Work without a widget rebuild. Empty business_id
  // would still POST — backend logs and shrugs.
  try {
    const apiBase = _vbApiBase(widget);
    const businessId = _vbBusinessId(widget);
    if (!apiBase || !businessId) return;
    await fetch(
      apiBase + '/admin/public/strain-event/' + encodeURIComponent(businessId),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: productId == null ? null : String(productId),
          action: action,
          session_id: _vbStrainSessionId(),
        }),
      }
    );
  } catch (e) {
    /* silent — analytics MUST NOT break UX */
  }
}

// Inline preview — calm, compact card rendered right under the product
// card that triggered it. User can click "See full profile" to open the
// full Infographic v2 modal. This is the default click behavior per user
// preference: "nice, calm" inline first, full modal on demand.
export async function toggleInlinePreview(widget, productId, productName, triggerBtn) {
  ensureStrainOverlayStyles();

  // Locate the product-card container so we can insert the preview as a
  // sibling below it (not inside the card's own flex row, which would
  // distort the card).
  const btnEl = triggerBtn;
  if (!btnEl) return;
  // User wants the preview to EXPAND THE CARD, not sit below it as a
  // separate card. Find the product card, append preview as its last
  // child so it flows inside the card's rounded border.
  const card = btnEl.closest('.premium-stream-card, .premium-product-card, .premium-message-bubble, .premium-message');
  if (!card) return;

  // Toggle: if this card already has a preview for THIS product, close.
  const existing = card.querySelector(':scope > .vb-strain-inline');
  if (existing && existing.getAttribute('data-product-id') === String(productId)) {
    existing.remove();
    btnEl.setAttribute('aria-expanded', 'false');
    return;
  }
  // Different product's preview in this card? Shouldn't happen (one
  // card = one product) but guard anyway.
  if (existing) existing.remove();

  const preview = document.createElement('div');
  preview.className = 'vb-strain-inline';
  preview.setAttribute('data-product-id', String(productId));
  // Strip the trailing ellipsis from the markup and add the
  // `--loading` modifier — CSS injects three pulsing dots via ::after.
  preview.innerHTML = '<div class="vb-strain-inline-empty vb-strain-inline-empty--loading">Loading strain info</div>';
  card.appendChild(preview);
  btnEl.setAttribute('aria-expanded', 'true');

  // Telemetry: customer opened the inline preview. Fire-and-forget so it
  // never delays the actual fetch/render below.
  recordStrainEvent(widget, 'open_inline', productId);

  const data = await _fetchStrainData(widget, productId);
  if (!data) {
    preview.innerHTML = '<div class="vb-strain-inline-empty">No curated data for this strain.</div>';
    return;
  }

  // ── Dual-strain shape detection ──────────────────────────────────────
  // When the API returns { primary, secondary } the product is a
  // documented cross. Stage 1 of the new tab pattern (May 2026 v3): show
  // a pill row with one pill per parent strain. Click a pill → opens the
  // Stage 3 overlay with that strain selected on the tab strip. No
  // intermediate compact preview — the inline area is purely a chooser.
  // Single-strain products keep the original inline-preview behavior
  // unchanged below.
  const _isDualShape = data && typeof data === 'object' &&
    data.primary && typeof data.primary === 'object' &&
    data.secondary && typeof data.secondary === 'object';
  if (_isDualShape) {
    // Prefer canonical_name so the pills read "Sour Diesel" / "Durban Poison",
    // not the full product_name with size + format suffix.
    const pName = (data.primary.canonical_name || data.primary.name || 'Strain A');
    const sName = (data.secondary.canonical_name || data.secondary.name || 'Strain B');
    let pillHtml = '';
    pillHtml += '<div class="teaser-pill-row">';
    pillHtml += '  <span class="teaser-pill-label">This is a cross — pick a parent strain to learn about</span>';
    pillHtml += '  <button type="button" class="teaser-pill" data-strain-key="primary">' + _escape(pName) + '</button>';
    pillHtml += '  <button type="button" class="teaser-pill" data-strain-key="secondary">' + _escape(sName) + '</button>';
    pillHtml += '</div>';
    preview.innerHTML = pillHtml;
    preview.querySelectorAll('.teaser-pill').forEach((pill) => {
      pill.addEventListener('click', (ev) => {
        ev.preventDefault(); ev.stopPropagation();
        const key = pill.getAttribute('data-strain-key');
        // Visually mark the chosen pill (consistent with the active tab
        // in stages 2 / 3) for the brief moment before the overlay opens.
        preview.querySelectorAll('.teaser-pill').forEach((p) => p.classList.remove('active'));
        pill.classList.add('active');
        openStrainOverlay(widget, productId, productName, data, key);
      });
    });
    return;
  }

  const s = _adaptStrainData(data, productName);
  const typeCls = _typeClass(s.typeConsensus.label);
  const topEffects = s.effects.slice(0, 3).map(e => e.name);
  const topFlavors = s.flavors.slice(0, 3).map(f => f.name);
  const desc = s.description ? _escape(_stripHtml(s.description)) : '';
  const isLong = s.description && s.description.length > 180;

  let html = '';
  // Source provenance note — user explicitly asked for clarity that this
  // data is NOT from the store itself, but also asked that no source name
  // OR count leaks to customers. Keep the generic disclaimer only.
  const sourceNote = 'Aggregated from independent online cannabis references — not from this store.';
  html += '<div class="vb-strain-inline-source-note">';
  html += '  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>';
  html += '  <span>' + _escape(sourceNote) + '</span>';
  html += '</div>';

  html += '<div class="vb-strain-inline-hdr">';
  html += '  <span class="vb-strain-inline-name">' + _escape(s.name) + '</span>';
  html += '  <span class="vb-strain-inline-type vb-strain-inline-type--' + typeCls + '">' + _escape(s.typeConsensus.label) + '</span>';
  html += '</div>';
  if (desc) {
    html += '<div class="vb-strain-inline-desc-wrap">';
    html += '  <div class="vb-strain-inline-desc">' + desc + '</div>';
    if (isLong) {
      html += '  <button type="button" class="vb-strain-inline-chev" aria-expanded="false" aria-label="Expand description">' +
              '<span class="vb-strain-inline-chev-label">Read more</span>' +
              '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>' +
              '</button>';
    }
    html += '</div>';
  }
  if (topEffects.length || topFlavors.length) {
    html += '<div class="vb-strain-inline-chips">';
    for (const e of topEffects) html += '<span class="vb-strain-inline-chip">' + _escape(e) + '</span>';
    for (const f of topFlavors) {
      html += '<span class="vb-strain-inline-chip vb-strain-inline-chip--flavor"' +
              ' data-flavor-name="' + _escape(f) + '" data-flavor-kind="flavor">' +
              _escape(f) + '</span>';
    }
    html += '</div>';
  }
  html += '<div class="vb-strain-inline-actions">';
  html += '  <button type="button" class="vb-strain-inline-full">See in-depth profile' +
          '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M9 5l7 7-7 7"/></svg>' +
          '</button>';
  html += '  <button type="button" class="vb-strain-inline-close" aria-label="Close">Close</button>';
  html += '</div>';
  preview.innerHTML = html;

  const descWrap = preview.querySelector('.vb-strain-inline-desc-wrap');
  const chevBtn = preview.querySelector('.vb-strain-inline-chev');
  if (chevBtn && descWrap) {
    const lbl = chevBtn.querySelector('.vb-strain-inline-chev-label');
    chevBtn.addEventListener('click', (ev) => {
      ev.preventDefault(); ev.stopPropagation();
      const open = descWrap.classList.toggle('open');
      chevBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (lbl) lbl.textContent = open ? 'Show less' : 'Read more';
    });
  }
  const fullBtn = preview.querySelector('.vb-strain-inline-full');
  if (fullBtn) {
    fullBtn.addEventListener('click', (ev) => {
      ev.preventDefault(); ev.stopPropagation();
      // Pass the full strain data we already fetched so openStrainOverlay
      // doesn't re-fetch, doesn't race, and can't silently fail on a 2nd
      // network call.
      openStrainOverlay(widget, productId, productName, data);
    });
  }
  const closeBtn = preview.querySelector('.vb-strain-inline-close');
  if (closeBtn) {
    closeBtn.addEventListener('click', (ev) => {
      ev.preventDefault(); ev.stopPropagation();
      preview.remove();
      btnEl.setAttribute('aria-expanded', 'false');
    });
  }
}

export async function openStrainOverlay(widget, productId, productName, preloadedData, initialActiveKey, opts) {
  ensureStrainOverlayStyles();

  // Render options (§3, 2026-06-04). Currently:
  //   showFaq — render the FAQ section. DEFAULT false, so the in-chat chatbot
  //             popup HIDES faqs (the new desired behavior). The standalone
  //             menu strain-reviews popup passes { showFaq: true } to show them.
  // Threaded into _renderInfographic / _renderTabbedInfographic below, which
  // gate the FAQ block on it.
  opts = opts || {};
  const _showFaq = opts.showFaq === true;
  // §10 — stacked radar labels are STANDALONE-POPUP-ONLY; chatbot keeps the
  // single combined meta line. Standalone index.js passes stackRadarLabels:true.
  const _stackRadar = opts.stackRadarLabels === true;

  // ─────────────────────────────────────────────────────────────────────
  // Phase 1 — INSTANT open with loading skeleton.
  // Previously this function awaited _fetchStrainData() BEFORE creating
  // any DOM, so the user saw a 1-2s "nothing happens" delay between
  // clicking the trigger and the overlay appearing. User asked for the
  // overlay to pop instantly with a clean spinner, then swap in real
  // content once the fetch resolves. We build the overlay shell + sticky
  // back-bar + skeleton body up front, append to DOM, and only then
  // kick off the network round-trip. The skeleton body is replaced
  // wholesale with the real infographic (or an error fallback) when
  // the fetch settles.
  // ─────────────────────────────────────────────────────────────────────

  // Attach overlay to the WIDGET ROOT (#chat-widget-premium), not chatBody.
  // chatBody is a scrolling container — a position:absolute overlay inside
  // it anchors to scrollable content's 0,0, which sits above the viewport
  // when the user has already scrolled down. That's why "See full profile"
  // looked like it did nothing: the overlay was rendering above the
  // visible area. Widget root is a fixed-size flex container that doesn't
  // scroll, so inset:0 fills the visible widget area reliably.
  const widgetRoot = widget.chatWidget
    || document.getElementById('chat-widget-premium')
    || (widget.chatBody && widget.chatBody.parentElement);
  if (!widgetRoot) return;

  const priorPosition = getComputedStyle(widgetRoot).position;
  if (priorPosition === 'static') widgetRoot.style.position = 'relative';

  const existing = widgetRoot.querySelector(':scope > .vb-strain-overlay');
  if (existing) existing.remove();

  const overlay = document.createElement('div');
  overlay.className = 'vb-strain-overlay';
  // Plumb the store's primary color into the overlay as a CSS custom
  // property so our themed thin scrollbar (defined in OVERLAY_CSS) picks
  // it up. Mirrors the dynamicCSS technique widget.js uses for
  // #chat-body-premium. Falls back to neutral via the var() default if
  // no theme color is available.
  const _vbThemePrimary = (widget && widget.themeEnabled
    && widget.themeConfig && widget.themeConfig.colors
    && widget.themeConfig.colors.primary)
    || (widget && widget.themeColors && widget.themeColors.primary_color)
    || null;
  if (_vbThemePrimary) {
    overlay.style.setProperty('--vb-strain-thumb', _vbThemePrimary);
    overlay.style.setProperty('--vb-strain-thumb-hover', _vbThemePrimary);
  }
  overlay.addEventListener('click', (e) => e.stopPropagation());
  overlay.addEventListener('wheel', (e) => { e.stopPropagation(); }, { passive: true });
  overlay.addEventListener('touchmove', (e) => { e.stopPropagation(); }, { passive: true });

  const popup = document.createElement('div');
  popup.className = 'vb-pop vb-pop--info2';

  // Sticky nav bar — back button (left), strain name (center, truncates),
  // visual spacer (right) for symmetry. Pins to the top of the scrolling
  // popup so the back action is always reachable while the user scrolls
  // through effects, terpenes, lineage, etc.
  //
  // Initial title uses productName until the fetch resolves and we know
  // the canonical strain name; _renderResolved() rewrites it then.
  // Visible during the loading skeleton too so the back button is
  // immediately available even before content loads.
  const stickyBar = document.createElement('div');
  stickyBar.className = 'info2-stickybar';
  stickyBar.innerHTML =
    '<button type="button" class="info2-stickybar-back" aria-label="Back">' +
    '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>' +
    '<span>Back</span>' +
    '</button>' +
    '<div class="info2-stickybar-title">' + _escape(productName || 'Strain') + '</div>' +
    '<div class="info2-stickybar-spacer" aria-hidden="true"></div>';
  stickyBar.querySelector('.info2-stickybar-back').addEventListener('click', (e) => {
    e.preventDefault(); e.stopPropagation();
    overlay.remove();
  });
  popup.appendChild(stickyBar);

  // Body starts as a skeleton (centered spinner + "Loading strain info...").
  // Replaced wholesale in _renderResolved() once the fetch settles.
  const body = document.createElement('div');
  body.className = 'info2-body';
  body.innerHTML =
    '<div class="info2-skeleton">' +
      '<div class="info2-skeleton-spinner" aria-hidden="true"></div>' +
      '<div class="info2-skeleton-text">Loading strain info...</div>' +
    '</div>';
  popup.appendChild(body);

  overlay.appendChild(popup);
  widgetRoot.appendChild(overlay);

  // Scrolled-state toggle on the sticky bar — gives it the shadow + hairline
  // + heavier title weight only once the user has scrolled, so the bar sits
  // flush at scroll-top and reads as a real pinned nav once content slides
  // beneath it. Listener target is POPUP (the actual scroll container after
  // the Option-A refactor) not overlay; sticky pins to popup's top so we
  // observe popup.scrollTop. No explicit teardown needed — listener dies
  // with the popup element.
  let _lastScrolledClass = false;
  const _onStickybarScroll = () => {
    const isScrolled = popup.scrollTop > 4;
    if (isScrolled !== _lastScrolledClass && stickyBar) {
      stickyBar.classList.toggle('info2-stickybar--scrolled', isScrolled);
      _lastScrolledClass = isScrolled;
    }
  };
  popup.addEventListener('scroll', _onStickybarScroll, { passive: true });

  // ─────────────────────────────────────────────────────────────────────
  // Phase 2 — fetch + render (or error fallback).
  // _renderResolved swaps the skeleton body for the real infographic and
  // wires every handler (orbital, flavor-tip, tab-swap, etc.). All paths
  // that could throw are wrapped so a render failure leaves the user with
  // a visible "Couldn't load strain info" + retry instead of an empty
  // overlay shell.
  // ─────────────────────────────────────────────────────────────────────

  const _renderError = (err) => {
    if (err) console.error('[vb-strain] openStrainOverlay render failed', { productId, productName, err });
    body.innerHTML =
      '<div class="info2-skeleton info2-skeleton--error">' +
        '<div class="info2-skeleton-text">Couldn’t load strain info — please try again.</div>' +
        '<button type="button" class="info2-skeleton-retry">Retry</button>' +
      '</div>';
    const retryBtn = body.querySelector('.info2-skeleton-retry');
    if (retryBtn) {
      retryBtn.addEventListener('click', (e) => {
        e.preventDefault(); e.stopPropagation();
        // Restore skeleton + kick off another fetch on the SAME overlay.
        body.innerHTML =
          '<div class="info2-skeleton">' +
            '<div class="info2-skeleton-spinner" aria-hidden="true"></div>' +
            '<div class="info2-skeleton-text">Loading strain info...</div>' +
          '</div>';
        _kickFetch();
      });
    }
  };

  // Terminal "no data" state — used when the backend returns a row but
  // the catalog has nothing meaningful to show (mega_source_count == 0
  // AND no description / effects / flavors populated). Different copy
  // from _renderError because retrying would just hit the same empty
  // row again — we offer Close instead of Retry. Also called from the
  // race between the bulk-prefetch (which hides empty-state buttons)
  // and a user clicking before the prefetch resolved.
  const _renderEmpty = () => {
    body.innerHTML =
      '<div class="info2-skeleton info2-skeleton--empty">' +
        '<div class="info2-skeleton-text">Strain details not available yet.</div>' +
        '<button type="button" class="info2-skeleton-close">Close</button>' +
      '</div>';
    const closeBtn = body.querySelector('.info2-skeleton-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault(); e.stopPropagation();
        overlay.remove();
      });
    }
  };

  // Decide whether the resolved data is "empty enough" to swap to the
  // friendlier no-data screen. Accepts EITHER shape (flat single-strain
  // or {primary, secondary} dual). Empty == mega_source_count is 0 AND
  // none of the meaningful payload buckets are populated. Each side of
  // a dual must be empty individually for the whole popup to be empty.
  const _isEmptyStrain = (d) => {
    if (!d || typeof d !== 'object') return true;
    const sc = (typeof d.mega_source_count === 'number') ? d.mega_source_count : 0;
    if (sc > 0) return false;
    const hasDesc = !!(d.description_polished || d.description);
    if (hasDesc) return false;
    const hasEffects = (Array.isArray(d.mega_effects_agg) && d.mega_effects_agg.length > 0)
                   || (Array.isArray(d.effects) && d.effects.length > 0);
    if (hasEffects) return false;
    const hasFlavors = (Array.isArray(d.mega_flavors_agg) && d.mega_flavors_agg.length > 0);
    if (hasFlavors) return false;
    const hasAromas = (Array.isArray(d.mega_aromas_agg) && d.mega_aromas_agg.length > 0);
    if (hasAromas) return false;
    const hasTerps = (Array.isArray(d.mega_terpenes) && d.mega_terpenes.length > 0)
                  || (Array.isArray(d.terpenes) && d.terpenes.length > 0);
    if (hasTerps) return false;
    const hasHelps = (Array.isArray(d.mega_helps_with_agg) && d.mega_helps_with_agg.length > 0);
    if (hasHelps) return false;
    return true;
  };

  const _renderResolved = (data) => {
    if (!data) {
      console.info('[strain-info] no curated data for', productId);
      _renderError(null);
      return;
    }

    // Catalog row exists but has nothing meaningful in it — show a
    // dedicated "not available yet" screen instead of the loading
    // spinner / generic error. Same path for flat + dual: dual is empty
    // only when BOTH sides are empty (one side documented is still
    // worth rendering). See _isEmptyStrain for the field checks.
    const isDualShapeEarly = !!(
      data.primary && typeof data.primary === 'object' &&
      data.secondary && typeof data.secondary === 'object'
    );
    const emptyOverall = isDualShapeEarly
      ? (_isEmptyStrain(data.primary) && _isEmptyStrain(data.secondary))
      : _isEmptyStrain(data);
    if (emptyOverall) {
      console.info('[strain-info] catalog row exists but no meaningful data for', productId);
      _renderEmpty();
      return;
    }

    // Refresh the triggering button's label with the now-known source
    // count. Cache is keyed by normalized strain name so opening "Sour
    // Diesel - Live Resin" populates the label on all other Sour Diesel
    // cards in the chat too. For dual-strain crosses we cache each
    // parent independently. Best-effort, never throws.
    try {
      if (data.primary && typeof data.primary === 'object' &&
          data.secondary && typeof data.secondary === 'object') {
        const pName = data.primary.canonical_name || data.primary.name || '';
        const sName = data.secondary.canonical_name || data.secondary.name || '';
        const pCount = (typeof data.primary.mega_source_count === 'number')
          ? data.primary.mega_source_count : 0;
        const sCount = (typeof data.secondary.mega_source_count === 'number')
          ? data.secondary.mega_source_count : 0;
        updateStrainTriggerLabels({ productId: productId, strainName: pName, count: pCount });
        updateStrainTriggerLabels({ strainName: sName, count: sCount });
      } else {
        const count = (typeof data.mega_source_count === 'number')
          ? data.mega_source_count : 0;
        const strainName = (data.canonical_name || data.name) || productName || '';
        updateStrainTriggerLabels({ productId: productId, strainName: strainName, count: count });
      }
    } catch (_e) { /* never block overlay render on label refresh */ }

    // Telemetry: customer opened the full in-depth profile overlay.
    // Fire-and-forget; never await before we render.
    recordStrainEvent(widget, 'open_full', productId);

    // Dual-strain detection — backend agent (May 2026) returns
    // { primary: {...}, secondary: {...} } when the product is a
    // documented cross like "Baked Alaska × Georgia Pie". Single-strain
    // products keep the legacy flat shape. We accept either.
    const isDualShape =
      data && typeof data === 'object' &&
      data.primary && typeof data.primary === 'object' &&
      data.secondary && typeof data.secondary === 'object';

    let s;
    let sPrimary = null, sSecondary = null;
    if (isDualShape) {
      sPrimary = _adaptStrainData(data.primary, productName);
      sSecondary = _adaptStrainData(data.secondary, data.secondary.name || '');
      // Build a synthetic top-level `s` so the downstream tooltip /
      // feedback handlers (flavor per-source tip, helpful row, orbital
      // — which is absent in dual mode but guarded) keep working
      // without each having to learn the dual shape. We merge per-
      // source maps from both sides so the flavor tooltip can attribute
      // a hover regardless of which column the chip came from. Names
      // use combined "A × B" for telemetry.
      const mergePerSource = (a, b) => {
        const out = {};
        if (a && typeof a === 'object') for (const k of Object.keys(a)) out[k] = a[k];
        if (b && typeof b === 'object') for (const k of Object.keys(b)) {
          out[out[k] ? (k + '__b') : k] = b[k];
        }
        return out;
      };
      s = {
        name: sPrimary.name + ' × ' + sSecondary.name,
        flavorsPerSource: mergePerSource(sPrimary.flavorsPerSource, sSecondary.flavorsPerSource),
        aromasPerSource: mergePerSource(sPrimary.aromasPerSource, sSecondary.aromasPerSource),
      };
    } else {
      s = _adaptStrainData(data, productName);
    }

    // Active tab key for the dual case. Defaults to "primary"; caller
    // can pass "secondary" via initialActiveKey to land directly on the
    // second strain (used by Stage 1 teaser pills).
    //
    // CRITICAL: declared BEFORE any reference to it — previous version
    // referenced activeKey in the sticky-bar initial-name expression
    // ABOVE its `let` declaration which hit the TDZ and threw
    // `ReferenceError: Cannot access 'activeKey' before initialization`.
    // That silent throw was the actual root cause of "dual-strain
    // button does nothing": the click handler in events.js fired
    // fine, openStrainOverlay started, the dual branch threw inside
    // sticky-bar render, no overlay was ever appended. Single-strain
    // never hit the bad branch so it appeared to work.
    let activeKey = (initialActiveKey === 'secondary') ? 'secondary' : 'primary';

    // Update sticky bar title now that we know the real strain name.
    // In dual mode pick the active side's clean name (sPrimary or
    // sSecondary) rather than the synthetic "A × B" combo built for
    // downstream helpers.
    try {
      const _initialStickyName = isDualShape
        ? ((activeKey === 'secondary' ? sSecondary : sPrimary) || {}).name
        : s.name;
      const _stickyTitleEl = stickyBar.querySelector('.info2-stickybar-title');
      if (_stickyTitleEl) {
        _stickyTitleEl.textContent = _initialStickyName || productName || 'Strain';
      }
    } catch (_e) { /* sticky title update is cosmetic, never fatal */ }

    // Swap skeleton body for the real infographic.
    body.innerHTML = isDualShape
      ? _renderTabbedInfographic(sPrimary, sSecondary, activeKey, { showFaq: _showFaq, stackRadarLabels: _stackRadar })
      : _renderInfographic(s, { showFaq: _showFaq, stackRadarLabels: _stackRadar });

    // Re-run the scrolled-state check now that body has real content
    // (the scroll position is still 0 but the popup's contentHeight
    // changed so layout may have shifted).
    _onStickybarScroll();

  // "Read more" toggle + feedback row — these attach handlers to fresh
  // DOM nodes inside the body, so they MUST re-bind after a tab swap
  // replaces body innerHTML. Wrapped in a function we can call once
  // initially and again whenever the user clicks an inactive strain
  // tab on a cross-strain popup.
  const _bindBodyTransientHandlers = () => {
    // "Read more" toggle for long descriptions.
    const descWrap = body.querySelector('.info2-desc-wrap[data-more="1"]');
    if (descWrap) {
      const btn = descWrap.querySelector('.info2-desc-toggle');
      const label = btn && btn.querySelector('.info2-desc-toggle-label');
      if (btn && !btn.dataset.vbBound) {
        btn.dataset.vbBound = '1';
        btn.addEventListener('click', (ev) => {
          ev.preventDefault(); ev.stopPropagation();
          const open = descWrap.classList.toggle('open');
          btn.setAttribute('aria-expanded', open ? 'true' : 'false');
          if (label) label.textContent = open ? 'Show less' : 'Read more';
        });
      }
    }

    // Helpful / Not helpful feedback row. Single-shot per overlay instance —
    // we flip data-voted="1" to hide the buttons and reveal the thanks msg
    // (CSS does the swap). Telemetry POST is fire-and-forget; UI doesn't
    // wait for the network round-trip.
    const fbRow = body.querySelector('.info2-feedback');
    if (fbRow && !fbRow.dataset.vbBound) {
      fbRow.dataset.vbBound = '1';
      const fbBtns = fbRow.querySelectorAll('.info2-fb-btn');
      const fbThanks = fbRow.querySelector('.info2-feedback-thanks');
      fbBtns.forEach((btn) => {
        btn.addEventListener('click', (ev) => {
          ev.preventDefault(); ev.stopPropagation();
          if (fbRow.getAttribute('data-voted') === '1') return;
          const dir = btn.getAttribute('data-fb');
          const action = dir === 'up' ? 'thumbs_up' : (dir === 'down' ? 'thumbs_down' : null);
          if (!action) return;
          fbRow.setAttribute('data-voted', '1');
          if (fbThanks) fbThanks.setAttribute('data-state', 'shown');
          fbBtns.forEach((b) => { b.setAttribute('disabled', 'disabled'); });
          recordStrainEvent(widget, action, productId);
        });
      });
    }
  };
  _bindBodyTransientHandlers();

  // Filterable orbital — chip toggles + node hover tooltip + click-to-
  // recenter with breadcrumb history. We rebind handlers each time we
  // re-render the orbital after a recenter (the section innerHTML is
  // replaced wholesale). Held in a closure so navigation history is
  // local to this strain popup and doesn't leak between sessions.
  //
  // Cross-strain note: when the user swaps tabs in a dual-strain popup
  // we reset navHistory to the new active strain's initial name. The
  // body-delegated click + hover handlers below survive innerHTML swaps,
  // so we only need to reset state — not re-attach.
  let _orbitNavHistory = [];
  let _orbitNavReset = (newInitialName) => {
    _orbitNavHistory.length = 0;
    if (newInitialName) _orbitNavHistory.push(newInitialName);
  };
  const orbitSection = body.querySelector('.info2-orbit-section');
  if (orbitSection) {
    const navHistory = _orbitNavHistory;  // shared ref for the closure
    const apiBase = (widget && widget.API_BASE)
      || (typeof window !== 'undefined' && window.CHATBOT_CONFIG && window.CHATBOT_CONFIG.API_URL)
      || (typeof window !== 'undefined' ? window.location.origin : '');

    const initialName = orbitSection.getAttribute('data-current-name');
    if (initialName) navHistory.push(initialName);

    const renderBreadcrumb = (section) => {
      const crumb = section.querySelector('.info2-orbit-breadcrumb');
      if (!crumb) return;
      let html = '';
      navHistory.forEach((name, i) => {
        const isLast = i === navHistory.length - 1;
        if (i > 0) html += '<span class="info2-orbit-breadcrumb-sep">›</span>';
        if (isLast) {
          html += '<span class="info2-orbit-breadcrumb-current">' + _escape(name) + '</span>';
        } else {
          html += '<span class="info2-orbit-breadcrumb-link" data-back-to="' + i + '">' + _escape(name) + '</span>';
        }
      });
      crumb.innerHTML = html;
    };

    const recenterTo = async (strainName) => {
      const section = body.querySelector('.info2-orbit-section');
      if (!section) return;
      const loader = section.querySelector('.info2-orbit-loading');
      if (loader) loader.setAttribute('data-state', 'visible');
      try {
        const resp = await fetch(apiBase + '/admin/public/strain-by-name/' +
                                  encodeURIComponent(strainName));
        const json = await resp.json();
        const d = json && json.data;
        if (!d) {
          if (loader) loader.setAttribute('data-state', 'hidden');
          return; // strain not in catalog — silently bail, keep current view
        }
        // Build minimal "s"-like inputs for _renderOrbital from the
        // by-name response shape.
        const newParents = (d.mega_parents || [])
          .map(p => ({ name: p.name || '',
                       sources: Array.isArray(p.sources) ? p.sources.length : 0 }))
          .filter(p => !_isPolluted(p.name));
        const newChildren = (d.mega_children || [])
          .map(c => c.name || '')
          .filter(name => name && !_isPolluted(name));
        const newOrbitalHtml = _renderOrbital(
          d.name, newParents, newChildren, d.mega_family_tree,
          d.strain_type, d.thc
        );
        // Replace the entire section innerHTML (drops loader naturally).
        // Preserve our nav history by re-rendering the breadcrumb after.
        const wrap = document.createElement('div');
        wrap.innerHTML = newOrbitalHtml;
        const newSection = wrap.firstChild;
        section.replaceWith(newSection);
        // Update navigation history (push the new strain) and re-render breadcrumb.
        if (navHistory[navHistory.length - 1] !== d.name) {
          navHistory.push(d.name);
        }
        renderBreadcrumb(newSection);
      } catch (e) {
        if (loader) loader.setAttribute('data-state', 'hidden');
      }
    };

    // Single delegated click handler — chip / node / breadcrumb-link.
    body.addEventListener('click', (ev) => {
      const t = ev.target;
      if (!t || !t.closest) return;
      const section = body.querySelector('.info2-orbit-section');
      if (!section || !section.contains(t)) return;

      // Chip toggle (filter generation visibility)
      const chip = t.closest('.info2-orbit-chip');
      if (chip) {
        ev.preventDefault(); ev.stopPropagation();
        const wasOn = chip.getAttribute('data-on') === 'true';
        const nowOn = !wasOn;
        chip.setAttribute('data-on', nowOn ? 'true' : 'false');
        const gen = chip.getAttribute('data-gen');
        const stage = section.querySelector('.info2-orbit-stage');
        if (!stage) return;
        stage.querySelectorAll('.info2-orbit-node[data-gen="' + gen + '"]')
          .forEach((n) => n.setAttribute('data-hidden', nowOn ? 'false' : 'true'));
        const ring = stage.querySelector('.info2-orbit-rings circle[data-gen="' + gen + '"]');
        if (ring) ring.setAttribute('data-hidden', nowOn ? 'false' : 'true');
        return;
      }

      // Breadcrumb back-navigation
      const back = t.closest('.info2-orbit-breadcrumb-link');
      if (back) {
        ev.preventDefault(); ev.stopPropagation();
        const idx = parseInt(back.getAttribute('data-back-to') || '0', 10);
        const targetName = navHistory[idx];
        if (!targetName) return;
        navHistory.length = idx; // truncate forward history
        recenterTo(targetName);  // recenterTo re-pushes onto history
        return;
      }

      // Node click → recenter on that strain
      const node = t.closest('.info2-orbit-node');
      if (node && node.getAttribute('data-hidden') !== 'true') {
        ev.preventDefault(); ev.stopPropagation();
        const name = node.getAttribute('data-name');
        if (name) recenterTo(name);
      }
    });

    // Hover tooltip — event-delegated on body so it survives section
    // replacement after a recenter.
    body.addEventListener('mouseover', (ev) => {
      const node = ev.target && ev.target.closest && ev.target.closest('.info2-orbit-node');
      if (!node || node.getAttribute('data-hidden') === 'true') return;
      const section = body.querySelector('.info2-orbit-section');
      const stage = section && section.querySelector('.info2-orbit-stage');
      const tooltip = stage && stage.querySelector('.info2-orbit-tip');
      if (!tooltip) return;
      const name = node.getAttribute('data-name') || '';
      const meta = node.getAttribute('data-meta') || '';
      tooltip.innerHTML = '<strong>' + _escape(name) + '</strong>' +
        (meta ? '<small>' + _escape(meta) + '</small>' : '');
      tooltip.style.left = node.style.left;
      tooltip.style.top = node.style.top;
      tooltip.setAttribute('data-state', 'visible');
    });
    body.addEventListener('mouseout', (ev) => {
      const node = ev.target && ev.target.closest && ev.target.closest('.info2-orbit-node');
      const related = ev.relatedTarget && ev.relatedTarget.closest
        ? ev.relatedTarget.closest('.info2-orbit-node') : null;
      if (!node || related) return;
      const section = body.querySelector('.info2-orbit-section');
      const stage = section && section.querySelector('.info2-orbit-stage');
      const tooltip = stage && stage.querySelector('.info2-orbit-tip');
      if (tooltip) tooltip.setAttribute('data-state', 'hidden');
    });
  }

  // ── Per-flavor source-attribution tooltip ────────────────────────────
  // Delegated handler on the popup body: hovering a flavor pill (or aroma
  // pill if present) pops a dark popover with per-source vote counts.
  // Source names are anonymized to "Source 1/2/…" before display — never
  // leak Leafly/Weedmaps/AllBud/etc. to customers (existing widget policy).
  //
  // CRITICAL: append to document.body, not the overlay. Any ancestor with
  // overflow:hidden / transform / filter / clip-path / contain creates a
  // new containing block that pins position:fixed children to its bounds.
  // Sites embedding the widget commonly wrap iframes in elements with
  // these properties, so a fixed-position tip parented INSIDE the overlay
  // can render off-screen or get clipped invisibly. document.body has no
  // such ancestors and position:fixed always resolves to the viewport
  // when parented there.
  //
  // Z-index is set inline to the max int so no host-site CSS rule can
  // shadow it. The widget root itself sits below this so the tip is
  // guaranteed to render above every other widget surface too.
  const _flavorTip = document.createElement('div');
  _flavorTip.className = 'info2-flavor-tip';
  _flavorTip.setAttribute('data-state', 'hidden');
  _flavorTip.style.zIndex = '2147483647';
  // Inherit the theme color CSS var from overlay if set, so the tip's
  // accent matches the rest of the popup once it's parented to body.
  if (_vbThemePrimary) {
    _flavorTip.style.setProperty('--vb-strain-thumb', _vbThemePrimary);
  }
  document.body.appendChild(_flavorTip);

  // Make sure the tip is torn down when the overlay closes (back button,
  // backdrop click, ESC, etc.). Without this the tip would outlive the
  // overlay since it's no longer a DOM child. We watch for overlay removal
  // via a MutationObserver on the widget root + a one-shot remove() patch
  // on overlay so the cleanup fires regardless of how the overlay dies.
  const _cleanupFlavorTip = () => {
    try { _flavorTip.remove(); } catch (_e) { /* already gone */ }
  };
  const _origOverlayRemove = overlay.remove.bind(overlay);
  overlay.remove = function () {
    _cleanupFlavorTip();
    return _origOverlayRemove();
  };

  // Stable anonymized label: hash the raw source keys alphabetically so
  // "leafly" is always Source 1 (or whatever its position is) within a
  // given strain. Doesn't matter which key maps to which number — only
  // matters that it's deterministic per render.
  const _buildSourceLabelMap = (perSourceObj) => {
    const m = {};
    if (!perSourceObj || typeof perSourceObj !== 'object') return m;
    const keys = Object.keys(perSourceObj).sort();
    keys.forEach((k, i) => { m[k] = 'Source ' + (i + 1); });
    return m;
  };
  const _flavorSourceLabels = _buildSourceLabelMap(s.flavorsPerSource);
  const _aromaSourceLabels = _buildSourceLabelMap(s.aromasPerSource);

  // Build tooltip HTML for a given flavor / aroma name. Always returns
  // a popup (never null) so the click affordance feels responsive even
  // when per-source data is missing. Per spec May 2026, this shows ONLY
  // the aggregate ("9 sources · 4,000 votes total") — no per-source
  // breakdown, no source labels. Customers don't care which scrapers
  // we pull from; they care about the strength of consensus.
  const _buildFlavorTipHTML = (name, kind) => {
    const perSource = (kind === 'aroma') ? s.aromasPerSource : s.flavorsPerSource;
    const lcName = String(name || '').trim().toLowerCase();
    if (!lcName) return null;

    let totalVotes = 0;
    let contributingSources = 0;
    if (perSource && typeof perSource === 'object') {
      for (const k of Object.keys(perSource)) {
        const list = perSource[k];
        if (!Array.isArray(list)) continue;
        for (const f of list) {
          if (f && typeof f === 'object' &&
              String(f.name || '').trim().toLowerCase() === lcName) {
            contributingSources += 1;
            totalVotes += Math.max(0, parseInt(f.votes || 0, 10) || 0);
            break;
          }
        }
      }
    }

    let html = '<div class="info2-flavor-tip-name">' + _escape(name) + '</div>';
    html += '<div class="info2-flavor-tip-rule"></div>';
    if (contributingSources === 0) {
      html += '<div class="info2-flavor-tip-foot-only">' +
              'Aggregated from cross-referenced sources</div>';
      return html;
    }
    const srcWord = contributingSources === 1 ? 'source' : 'sources';
    if (totalVotes > 0) {
      html += '<div class="info2-flavor-tip-foot-only">' +
              contributingSources + ' ' + srcWord + ' · ' +
              totalVotes.toLocaleString() + ' votes total</div>';
    } else {
      html += '<div class="info2-flavor-tip-foot-only">' +
              contributingSources + ' ' + srcWord + '</div>';
    }
    return html;
  };

  // Position tip above the pill (in viewport coords — fixed positioning).
  // Falls back below the pill if it'd clip the top of the screen.
  const _positionFlavorTip = (pill) => {
    if (!pill) return;
    const r = pill.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    let cy = r.top;  // tip CSS uses translate(-50%, calc(-100% - 8px))
    // If too close to top of viewport, flip below the pill.
    if (cy < 90) {
      cy = r.bottom + 8;
      _flavorTip.style.transform = 'translate(-50%, 0)';
    } else {
      _flavorTip.style.transform = '';  // fall back to CSS default (above)
    }
    _flavorTip.style.left = cx + 'px';
    _flavorTip.style.top = cy + 'px';
  };

  const _showFlavorTip = (pill) => {
    const name = pill.getAttribute('data-flavor-name');
    const kind = pill.getAttribute('data-flavor-kind') || 'flavor';
    if (!name) return;
    const html = _buildFlavorTipHTML(name, kind);
    if (!html) return;
    _flavorTip.innerHTML = html;
    _positionFlavorTip(pill);
    _flavorTip.setAttribute('data-state', 'visible');
  };
  const _hideFlavorTip = () => {
    _flavorTip.setAttribute('data-state', 'hidden');
  };

  // Desktop hover — use mouseover / mouseout (bubble-phase) with
  // closest('[data-flavor-name]') so the handler works regardless of
  // whether the event fires on the pill itself or any text node child.
  //
  // Previous implementation used capture-phase mouseenter/mouseleave on
  // body. Per spec these events DO NOT propagate at all (even in capture),
  // and Chrome on Windows-laptop in particular failed to deliver them to
  // delegated listeners — the popup worked on phone (touchstart path)
  // but never on PC mouse hover. mouseover/mouseout DO bubble and are
  // the standard delegation pattern.
  //
  // The relatedTarget guard on mouseout avoids flicker: when the mouse
  // moves from a child text node back to the pill (or to another node
  // still inside the same pill), we'd otherwise hide-then-show on every
  // sub-pixel move. Pills today have no children, but this is cheap
  // insurance against future markup changes.
  body.addEventListener('mouseover', (ev) => {
    const t = ev.target;
    if (!t || !t.closest) return;
    const pill = t.closest('[data-flavor-name]');
    if (!pill || !body.contains(pill)) return;
    // If we crossed into this pill from inside the same pill, do nothing
    // (avoid re-positioning the tip on every internal mouseover).
    const from = ev.relatedTarget;
    if (from && from.nodeType === 1 && from.closest &&
        from.closest('[data-flavor-name]') === pill) return;
    _showFlavorTip(pill);
  });
  body.addEventListener('mouseout', (ev) => {
    const t = ev.target;
    if (!t || !t.closest) return;
    const pill = t.closest('[data-flavor-name]');
    if (!pill) return;
    // If we're moving INTO the same pill (or a child of it), don't hide.
    const to = ev.relatedTarget;
    if (to && to.nodeType === 1 && to.closest &&
        to.closest('[data-flavor-name]') === pill) return;
    _hideFlavorTip();
  });
  // Mobile / touch + desktop click: tap/click shows the tooltip briefly
  // (1.6s) then auto-hides. Desktop hover (mouseover above) handles the
  // hover affordance; click is a separate path because:
  //   - some users only click (don't hover-and-wait)
  //   - touchscreen laptops fire `click` but no real `mouseover`
  //   - trackpad users without three-finger-hover don't trigger mouseover
  // Both share one auto-hide timer so back-to-back taps/clicks reset it.
  let _flavorTipAutoHideTimer = null;
  const _scheduleAutoHide = () => {
    if (_flavorTipAutoHideTimer) clearTimeout(_flavorTipAutoHideTimer);
    _flavorTipAutoHideTimer = setTimeout(_hideFlavorTip, 2400);
  };
  body.addEventListener('touchstart', (ev) => {
    const pill = ev.target && ev.target.closest && ev.target.closest('[data-flavor-name]');
    if (!pill) return;
    _showFlavorTip(pill);
    _scheduleAutoHide();
  }, { passive: true });
  body.addEventListener('click', (ev) => {
    const pill = ev.target && ev.target.closest && ev.target.closest('[data-flavor-name]');
    if (!pill || !body.contains(pill)) return;
    _showFlavorTip(pill);
    _scheduleAutoHide();
  });
  // Hide the tip on any scroll inside the popup (the actual scroll
  // container after the Option-A sticky refactor) — pill moves, tip
  // would be misaligned otherwise. Guarded: only hide if the tip was
  // actually visible. Some laptops fire phantom scroll events on layout
  // reflow caused by the tip's own opacity transition, which would race
  // the show and leave the user with no tip at all.
  popup.addEventListener('scroll', () => {
    if (_flavorTip.getAttribute('data-state') === 'visible') _hideFlavorTip();
  }, { passive: true });

  // ── Tab swap handler (dual-strain popups only) ─────────────────────
  // Click an inactive .info2-strain-tab → re-render the body with the
  // other strain. No fetch — both blobs live in memory. Direct handlers
  // on the body's transient nodes (Read more, feedback) get re-bound;
  // body-delegated handlers (orbital, flavor-tip) survive the innerHTML
  // swap. We update tab .active classes, reset orbital nav history, and
  // hide any stuck flavor-tip from the previous strain.
  if (isDualShape) {
    body.addEventListener('click', (ev) => {
      const t = ev.target;
      if (!t || !t.closest) return;
      const tab = t.closest('.info2-strain-tab');
      if (!tab || !body.contains(tab)) return;
      const key = tab.getAttribute('data-strain-key');
      if (!key || (key !== 'primary' && key !== 'secondary')) return;
      if (key === activeKey) return;  // already active, nothing to do
      ev.preventDefault(); ev.stopPropagation();
      activeKey = key;
      const next = (activeKey === 'primary') ? sPrimary : sSecondary;
      body.innerHTML = _renderTabbedInfographic(sPrimary, sSecondary, activeKey, { showFaq: _showFaq, stackRadarLabels: _stackRadar });
      _bindBodyTransientHandlers();
      _orbitNavReset(next && next.name ? next.name : null);
      _hideFlavorTip();
      // Sticky bar title tracks the active strain so the back button's
      // header label matches what the user is currently reading. Without
      // this, the title would stay frozen on the strain that was active
      // when the overlay first opened.
      const _stickyTitle = stickyBar.querySelector('.info2-stickybar-title');
      if (_stickyTitle && next && next.name) {
        _stickyTitle.textContent = next.name;
      }
    });
  }
  };  // end _renderResolved

  // Fire the fetch then render. Wrapped in try/catch so any throw (network
  // failure, JSON parse, downstream render bug) hits the visible error
  // fallback instead of leaving the user with an empty overlay shell.
  // _renderError logs to console with full context for diagnosis.
  const _kickFetch = async () => {
    try {
      const data = preloadedData || await _fetchStrainData(widget, productId);
      _renderResolved(data);
    } catch (err) {
      _renderError(err);
    }
  };
  _kickFetch();
}

// Format the dynamic "Curated strain info · N sources" label for the
// product-card trigger button. Returns the bare "Curated strain info"
// string when count is missing/zero so the button stays meaningful even
// before we've fetched mega data for the strain. Singularizes "source"
// when N === 1.
export function formatStrainTriggerLabel(count) {
  const n = (typeof count === 'number' && count > 0) ? Math.floor(count) : 0;
  if (n <= 0) return 'Curated strain info';
  return 'Curated strain info · ' + n + (n === 1 ? ' source' : ' sources');
}

// sessionStorage key for cached source counts. Scoped per browser session
// so the count survives page reloads / SPA route changes but doesn't leak
// across business contexts (sessionStorage is per-tab anyway).
//
// Two key shapes are used (write both, read either):
//   vb-strain-srccount:s:<normalized_strain_name>  <-- PRIMARY. Shared
//     across every product that references the same strain, so when the
//     user opens "Sour Diesel - Live Resin" the count populates the
//     trigger label on all 6 other Sour Diesel products in the chat too.
//   vb-strain-srccount:p:<product_id>              <-- LEGACY / FALLBACK.
//     Kept so any pre-existing cached entries (from the previous
//     productId-keyed cache) still resolve until the next strain fetch
//     migrates them.
const _SRC_COUNT_CACHE_PREFIX = 'vb-strain-srccount:';

// Normalize a strain name to a stable cache key. Case-insensitive,
// punctuation/whitespace collapsed to underscores, trim leading/trailing
// underscores. e.g. "Sour Diesel" / "sour-diesel" / "SOUR  DIESEL!"
// all collapse to "sour_diesel".
function _normStrainName(s) {
  if (!s || typeof s !== 'string') return '';
  return s.toLowerCase()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_+|_+$/g, '');
}

// Internal: read a strain-keyed cached count. Returns 0 when missing /
// unavailable so callers can Math.max it freely.
function _readCachedCount(strainName) {
  const k = _normStrainName(strainName);
  if (!k) return 0;
  try {
    if (typeof sessionStorage === 'undefined') return 0;
    const v = sessionStorage.getItem(_SRC_COUNT_CACHE_PREFIX + 's:' + k);
    if (v == null) return 0;
    const n = parseInt(v, 10);
    return (Number.isFinite(n) && n > 0) ? n : 0;
  } catch (_e) { return 0; }
}

// Read the cached mega_source_count for a strain (preferred) or product
// (legacy fallback). Returns 0 when nothing is cached — formatStrainTriggerLabel
// treats 0 as "no count yet" and renders the bare label.
//
// For dual-strain products, pass both strain names and we return
// max(primary, secondary) — surfaces the better-documented parent so the
// trigger is never blank when at least one side has data.
export function getCachedSourceCount(arg) {
  // Back-compat: previous signature was getCachedSourceCount(productId).
  // Accept a bare string/number as productId-only and dispatch through.
  let productId, strainName, secondStrainName;
  if (arg && typeof arg === 'object') {
    productId = arg.productId;
    strainName = arg.strainName;
    secondStrainName = arg.secondStrainName;
  } else {
    productId = arg;
  }
  try {
    if (secondStrainName) {
      const a = _readCachedCount(strainName);
      const b = _readCachedCount(secondStrainName);
      const m = Math.max(a || 0, b || 0);
      if (m > 0) return m;
    } else if (strainName) {
      const c = _readCachedCount(strainName);
      if (c > 0) return c;
    }
    if (productId != null && typeof sessionStorage !== 'undefined') {
      const v = sessionStorage.getItem(_SRC_COUNT_CACHE_PREFIX + 'p:' + String(productId));
      if (v != null) {
        const n = parseInt(v, 10);
        if (Number.isFinite(n) && n > 0) return n;
      }
    }
  } catch (_e) { /* storage disabled */ }
  return 0;
}

// Update every .vb-strain-learn-link trigger on the page that points at
// the given strain (preferred) or productId (fallback) so the visible
// label shows the now-known mega_source_count. Used by openStrainOverlay()
// once the fetch resolves: first click on a strain populates the count on
// EVERY card showing that strain, not just the one the user clicked.
//
// Also writes the count to sessionStorage so it survives page reloads;
// widget.js consults getCachedSourceCount() at button-render time.
//
// Accepts the legacy positional signature (productId, count) for back-compat
// with any caller that hasn't been migrated yet.
export function updateStrainTriggerLabels(arg, maybeCount) {
  let productId, strainName, count;
  if (arg && typeof arg === 'object') {
    productId = arg.productId;
    strainName = arg.strainName;
    count = arg.count;
  } else {
    productId = arg;
    count = maybeCount;
  }
  if (typeof document === 'undefined') return;
  const n = (typeof count === 'number' && count > 0) ? Math.floor(count) : 0;
  // Persist by strain name (preferred) AND by product_id (legacy/fallback)
  try {
    if (typeof sessionStorage !== 'undefined') {
      if (n > 0 && strainName) {
        const k = _normStrainName(strainName);
        if (k) sessionStorage.setItem(_SRC_COUNT_CACHE_PREFIX + 's:' + k, String(n));
      }
      if (n > 0 && productId != null) {
        sessionStorage.setItem(_SRC_COUNT_CACHE_PREFIX + 'p:' + String(productId), String(n));
      }
    }
  } catch (_e) { /* storage full / disabled, non-fatal */ }
  // Rewrite every visible trigger that matches THIS strain OR this product.
  // For dual-strain cards, the shown count is max(this strain, other strain)
  // re-read from the cache — the other side may already be populated from a
  // prior fetch even if this call only knows one half.
  try {
    const pidStr = productId != null ? String(productId) : '';
    const strainKey = _normStrainName(strainName);
    document.querySelectorAll('.vb-strain-learn-link').forEach((btn) => {
      const bName = btn.getAttribute('data-strain-name') || '';
      const bSecond = btn.getAttribute('data-second-strain-name') || '';
      const bPid = btn.getAttribute('data-product-id') || '';
      const matchesStrain = strainKey && (
        (bName && _normStrainName(bName) === strainKey) ||
        (bSecond && _normStrainName(bSecond) === strainKey)
      );
      const matchesPid = pidStr && bPid && bPid === pidStr;
      if (!matchesStrain && !matchesPid) return;
      // Compute final shown count: for dual cards, take max across both
      // halves so we don't blank out a card whose other half already has a
      // cached count.
      let finalCount = n;
      if (bSecond) {
        const a = _readCachedCount(bName);
        const b = _readCachedCount(bSecond);
        finalCount = Math.max(a || 0, b || 0, finalCount);
      } else if (bName) {
        const a = _readCachedCount(bName);
        finalCount = Math.max(a || 0, finalCount);
      }
      const txt = formatStrainTriggerLabel(finalCount);
      const span = btn.querySelector('.vb-strain-learn-text');
      if (span && span.textContent !== txt) span.textContent = txt;
    });
  } catch (_e) { /* DOM gone, never fatal */ }
}

export const STRAIN_INDEXED_CATEGORIES = new Set([
  'Flower', 'Flowers',
  'Pre-Roll', 'Pre-Rolls', 'Preroll', 'Prerolls',
  'Concentrate', 'Concentrates',
  'Vape', 'Vapes', 'Vaporizer', 'Vaporizers',
]);

export function isStrainIndexedCategory(cat) {
  return STRAIN_INDEXED_CATEGORIES.has(cat || '');
}
